<template>
  <!--
    Claude Design redesign experiment: the original template (v-tabs +
    v-window + Exit & Save dialog) has been replaced with the new
    CampaignEditShell (left sidebar, hash routing, autosave, revert).

    The script section below is mostly untouched: data loading, save
    methods, tab validation logic, refs, and event handlers all still
    work because the section components (campaign-setup, etc.) are
    rendered into named slots on the shell, with the same props and
    @event bindings as before.

    Section-id ↔ tabName mapping (see updated tabNames data prop):
      review        — synthetic landing
      name          → campaignSetup       (tab 0)
      client        → clientContact          (tab 1)
      branding      → preview             (tab 11; storefront design)
      login         → shopper             (tab 2)
      departments   → departments         (tab 5)
      products      → products            (tab 10)
      decorations   → decorations         (tab 9)
      payment       → features            (tab 3; the kitchen-sink)
      tax           → checkoutInfo        (tab 4)
      managers      → managers            (tab 7)

    Removed from editor (lived only on the old tab bar):
      orders, reports, the original storefront-preview tab (replaced by
      drawer), the inline Exit & Save flow.

    Dropped from the sidebar (tab slot kept inert for positional indexing):
      fulfillment (tab 8) — rendered an empty panel after the pick/pack and
      Pickup Locations surfaces were removed; the section was dropped.
  -->
  <div class="campaign-creator-container">
    <campaign-edit-shell
      v-if="isMounted"
      :campaign="campaign"
      :edit-mode="editMode"
      :section-status="sectionStatus"
      :section-subtitle="sectionSubtitle"
      @active-section-changed="onActiveSectionChanged"
      @navigate-section="createThenNavigate"
      @revert-completed="onRevertCompleted"
    >
      <!--
        A refused save, said on every section: the server's reason, verbatim.
        For a Store Login rule the settings have already gone back to what
        is saved (Bug Intake #344, Jake 2026-10-15); any other refusal (a
        bad name, a date) stays on screen for the owner to fix.
      -->
      <template #banner>
        <!--
          An ARCHIVED store opens in a restricted, Batches-only mode (Jake,
          2026-10-20): this banner says so and carries Unarchive. Coming back
          from it reloads the store, which restores the full editor.
        -->
        <archived-store-banner
          v-if="campaign && campaign.archived_at && campaign._jv"
          :store-id="campaign._jv.id"
          :store-name="campaign.name"
          @unarchived="onUnarchived"
        />
        <v-alert
          v-if="saveRefusal"
          type="warning"
          variant="tonal"
          density="comfortable"
          class="mb-4"
          data-testid="store-save-refused"
        >
          {{ saveRefusal }}
        </v-alert>
        <v-alert
          v-if="autoSaveHalted"
          type="error"
          variant="tonal"
          density="comfortable"
          class="mb-4"
          data-testid="store-autosave-halted"
        >
          Autosave stopped: the editor kept saving with no new edits from
          you. Reload the page to see what was saved. Your next edit tries
          again.
        </v-alert>
      </template>
      <!--
        Client & Store: the legacy Name & Launch ("name") and Client Info
        ("client") sections merged into one nested-route section
        (client-store).
        PR B1 (Store Editor Redesign Closing Chain): StoreSetup now splits
        its content into two EditorCard wraps -- "Client company" and
        "Store details & launch". The Admin/Buyer fields belong inside
        the second card, so we slot StoreClientContact.vue into StoreSetup's
        `client-contact` named slot instead of rendering it as a sibling.
      -->
      <template #section-client-store>
        <div v-if="campaignActivated" class="disable-form-layer"></div>
        <campaign-setup
          ref="campaignSetup"
          :campaign="campaign"
          :editMode="editMode"
          @campaign-changed="campaignChanged"
          @update-tab-invalid="updateTabInvalid"
        >
          <template #client-contact>
            <campaign-client-contact
              ref="clientContact"
              :campaign="campaign"
              :editMode="editMode"
              :persisted-client-id="$data._persistedClientId"
              @client-unlinked="onClientUnlinked"
              @client-company-changed="onClientCompanyChanged"
              :targetRole="roles['Client Admin']"
              :otherRole="roles['Shopper']"
              @campaign-changed="campaignChanged"
              @update-tab-invalid="updateTabInvalid"
              @updateEmail="emailError = null"
              :emailError="emailError"
            />
          </template>
        </campaign-setup>
      </template>

      <template #section-branding>
        <div v-if="campaignActivated" class="disable-form-layer"></div>
        <store-branding
          ref="preview"
          :campaign="campaign"
          :editMode="editMode"
          @campaign-changed="campaignChanged"
          @update-tab-invalid="updateTabInvalid"
        />
      </template>

      <template #section-store-login>
        <div v-if="campaignActivated" class="disable-form-layer"></div>
        <campaign-shopper
          ref="shopper"
          :campaign="campaign"
          :saved-access="savedAccess"
          :editMode="editMode"
          :targetRole="roles['Shopper']"
          :otherRole="roles['Client Admin']"
          @campaign-changed="campaignChanged"
          @update-tab-invalid="updateTabInvalid"
        />
      </template>

      <template #section-products>
        <div v-if="campaignActivated" class="disable-form-layer"></div>
        <campaign-products
          ref="products"
          :campaign="campaign"
          :editMode="editMode"
          @campaign-changed="changeCampaignProducts"
          @category-changed="campaignChanged"
          @product-upload="setActionButtonsStatus"
          @update-tab-invalid="updateTabInvalid"
          @setBackgroundColor="setBackgroundColor"
        />
      </template>

      <template #section-decorations>
        <div v-if="campaignActivated" class="disable-form-layer"></div>
        <campaign-decorations
          ref="decorations"
          :campaign="campaign"
          :editMode="editMode"
          @campaign-changed="campaignChanged"
          @update-tab-invalid="updateTabInvalid"
        />
      </template>

      <!--
        C1 (2026-07-24, ADR 0059): Categories & Storefront page shell.
        Like store-defaults-section, this stands apart from the legacy
        tabNames/tab-index bridge -- _sectionIdToTabName returns null for
        "categories" (no legacy tab counterpart), so the parent's
        validate/save sweep never touches it via the legacy path. It
        still implements updateCampaign()/validateTab() directly (both
        no-ops in C1) so a future full-tree save call finds them present.
      -->
      <template #section-categories>
        <div v-if="campaignActivated" class="disable-form-layer"></div>
        <store-categories
          v-if="editMode && campaign && campaign._jv && campaign._jv.id"
          ref="categories"
          :campaign="campaign"
          :editMode="editMode"
          @campaign-changed="campaignChanged"
        />
      </template>

      <template #section-pricing>
        <div v-if="campaignActivated" class="disable-form-layer"></div>
        <!--
          Store Pricing grid (Phil's 2026-07-18 prototype). This panel owns
          its own GET/PATCH against /api/stores/<id>/pricing-grid/ rather
          than riding the campaign autosave, so it mounts only once the
          store is persisted (a store id is required to address the
          endpoint) and the parent's validate/save sweep never touches it.

          It is the FIRST card in the section -- above campaign-features
          (Payments & Fundraiser) as well as Shopper Allowance. Jake,
          2026-08-26, after retesting: "one of the most important settings
          here", and TRULY first, not merely ahead of Allowance (PR #2938
          ordered only the two self-hosted cards and left this one second).

          Reordering is safe because StoreAddEdit reaches every section
          through ``this.$refs[name]`` -- driven by the ``tabNames`` array
          in tabRef()/_sectionIdToTabName() -- and never by DOM position.
          campaign-features keeps ref="features" and its slot in tabNames,
          so validate/save/updateCampaign resolve it exactly as before.
          Nothing below it reads a value this card initialises: the two
          self-hosted cards own their own endpoints, take no props from
          features, and StoreFeatures only emits UPWARD (campaign-changed,
          update-tab-invalid) with no provide/inject in either direction.

          Both self-hosted cards carry ``pricing-section-card``. Each one
          is wrapped in its own <div>, so EditorCard's
          ``.editor-card + .editor-card`` adjacency rule never sees them as
          siblings and never fired -- which is why the two cards butted
          together with no gap. The wrappers are what has to be spaced.
        -->
        <div
          v-if="editMode && campaign && campaign._jv && campaign._jv.id"
          class="pricing-section-card"
          data-testid="pricing-grid-card"
        >
          <store-pricing-grid
            :store-id="campaign._jv.id"
            :refresh-key="feeWarningRefreshKey"
          />
        </div>

        <campaign-features
          ref="features"
          :campaign="campaign"
          :editMode="editMode"
          @campaign-changed="campaignChanged"
          @update-tab-invalid="updateTabInvalid"
        />
        <!--
          Allowance v1->v2 migration Slice 3 (2026-07-09): the v2 Shopper
          Allowance card lives in the Pricing section (moved from Team &
          Departments). It owns its own GET/PUT against
          /api/stores/<id>/allowance-policy/ (not part of campaign autosave),
          so it only mounts once the store is persisted (store id required to
          address the endpoint). The card configures amount + cadence only --
          cap-vs-hybrid is DERIVED from the payment method (the CC-vs-payroll
          radio in the Payments card above), NOT a per-card overflow toggle.
          Design doc:
          docs/planning/2026-07-09-payment-model-and-legacy-allowance-retirement.md
        -->
        <div
          v-if="editMode && campaign && campaign._jv && campaign._jv.id"
          class="pricing-section-card"
          data-testid="pricing-allowance-policy-card"
        >
          <!-- A saved allowance changes the platform-fee warning, which lives
               only in the pricing grid's fee section (decision #8). -->
          <store-allowance-policy
            :store-id="campaign._jv.id"
            :access-mode="campaign.access_mode"
            @saved="onAllowanceSaved"
          />
        </div>
      </template>

      <!--
        Batch Management (2026-08-20, ADR 0066). Like store-allowance-policy
        above, this section owns its own endpoints rather than riding the
        campaign autosave, so it mounts only once the store is persisted (a
        store id is required to address /batch-management/stores/<id>/...).
        It therefore stands outside the legacy tabNames/tab-index bridge --
        _sectionIdToTabName returns null for "batches" -- and the parent's
        validate/save sweep never touches it.

        There is nothing to validate or save on this surface: batches are
        created by the store, not by the distributor, and every mutation
        (release, close, edit, cancel) is an explicit, individually
        confirmed action rather than a form field.
      -->
      <template #section-batches>
        <batches-panel
          v-if="editMode && campaign && campaign._jv && campaign._jv.id"
          :store-id="campaign._jv.id"
          :rules-locked="!!campaign.archived_at"
        />
        <div v-else class="bm-needs-store" data-testid="batches-needs-store">
          Batches appear here once the store is saved. The store opens the first
          batch when a shopper places an order -- you do not create them by hand.
        </div>
      </template>

      <!--
        PR-G (2026-06-22) Checkout IA refactor: the new Checkout section
        is an orchestrator (StoreCheckout) that mounts three subordinate
        editor cards alongside the legacy StoreCheckoutInfo (Identity /
        Custom fields / Shipping):
          - Shopper Allowance (lifted from Pricing -> StorePaymentOptions)
          - Store Limits & Security (lifted from Pricing -> StoreFeatures)
        StoreCheckout exposes the same orchestrator surface as the prior
        ``ref="checkoutInfo"`` mount so the parent autosave + validation
        wiring (``checkoutInfo`` in tabNames + sectionStatus map) keeps
        working unchanged.
      -->
      <template #section-checkout>
        <div v-if="campaignActivated" class="disable-form-layer"></div>
        <store-checkout
          ref="checkoutInfo"
          :campaign="campaign"
          :editMode="editMode"
          @set-department-enabled="setDepartmentsEnabled"
          @set-manager-enabled="setManagersEnabled"
          @campaign-changed="campaignChanged"
          @checkout-fields-validate="validateCheckoutFields"
          @update-tab-invalid="updateTabInvalid"
        />
      </template>

      <!--
        Phase 4.11 (route fork): Store defaults section. Stands apart
        from the store's metadata save flow -- the section owns its own
        GET/PATCH against /api/stores/<id>/defaults/, so there is no
        @campaign-changed wiring and the save here doesn't intermix
        with the parent's persistence. Only rendered in edit mode, since
        the store id is required to address the endpoint.
      -->
      <template #section-defaults>
        <div v-if="campaignActivated" class="disable-form-layer"></div>
        <store-defaults-section
          v-if="editMode && campaign && campaign._jv && campaign._jv.id"
          :store-id="campaign._jv.id"
        />
      </template>

      <template #section-tax-legal>
        <div v-if="campaignActivated" class="disable-form-layer"></div>
        <campaign-tax-legal
          ref="taxLegal"
          :campaign="campaign"
          :editMode="editMode"
          @campaign-changed="campaignChanged"
          @update-tab-invalid="updateTabInvalid"
        />
      </template>

      <template #section-notifications>
        <div v-if="campaignActivated" class="disable-form-layer"></div>
        <campaign-email-config
          ref="emailConfig"
          :campaign="campaign"
          :edit-mode="editMode"
          :disable-fields="disableFlags.disableForCampaignLifecycle"
          :prevent-order-duplication="!!campaign.is_prevent_order_duplication"
          @campaign-changed="campaignChanged"
        />
      </template>

      <!--
        Team & Departments: the legacy Managers ("managers") and
        Departments ("departments") sections merged into one nested-route
        section (team). Each child component renders its OWN EditorCard
        (StoreManagers => "Team", StoreDepartments => "Departments")
        so we don't wrap them again here. PR G2 cleanup (task #111)
        dropped the duplicate outer v-card chrome; pre-G2 the "Managers"
        outer title fought with the inner "Team" title for primacy.
        The data-testid markers stay on a thin wrapper div so the
        layout grid + the existing per-section refs are unchanged.
      -->
      <template #section-team>
        <div v-if="campaignActivated" class="disable-form-layer"></div>
        <div class="team-section-card" data-testid="team-managers-card">
          <campaign-managers
            ref="managers"
            :campaign="campaign"
            :editMode="editMode"
            @campaign-changed="campaignChanged"
            @update-tab-invalid="updateTabInvalid"
          />
        </div>
        <!--
          Client Portal v2 (PR B): the new department/budget/allowance
          config surfaces. Department MAPPING (which of the client's
          departments apply to this store) + the per-store allowance
          POLICY. Departments themselves are authored on the client page
          (ClientDepartmentsPanel). These cards have their own GET/PUT
          endpoints, so they only mount once the store (and its client)
          are persisted.

          (PR E removed the legacy per-store "Departments" card that wrote
          the dropped ``stores.departments`` JSON column; the mapping card
          below is its replacement.)
        -->
        <div
          v-if="editMode && campaign && campaign._jv && campaign._jv.id && campaign.client_id && enableDepartments"
          class="team-section-card"
          data-testid="team-v2-department-mapping-card"
        >
          <store-department-mapping
            :store-id="campaign._jv.id"
            :client-id="campaign.client_id"
          />
        </div>
        <!--
          Allowance v1->v2 migration Slice 3 (2026-07-09): the Shopper
          Allowance card (StoreAllowancePolicy) moved OUT of this Team &
          Departments section into the Pricing section (#section-pricing),
          co-located with the payment-config surface + the OrderLimits gate
          that already reads the v2 allowance state. Design doc:
          docs/planning/2026-07-09-payment-model-and-legacy-allowance-retirement.md
        -->
      </template>

      <template #section-review="{ openPreview }">
        <campaign-review-launch
          :campaign="campaign"
          :section-status="sectionStatus"
          :section-subtitle="sectionSubtitle"
          @go-to-section="goToSection"
          @preview="openPreview"
          @launch="toggleStoreActive(true)"
        />
      </template>
    </campaign-edit-shell>

    <!-- Loading state shown while data loads -->
    <div v-else class="campaign-loading-state" style="padding: 64px; text-align: center;">
      <v-progress-circular indeterminate color="primary" />
      <div style="margin-top: 16px; color: var(--tf-fg-4);">Loading store…</div>
    </div>

    <!--
      Unsaved-changes navigation guard modal. Rendered as a plain in-tree
      overlay (not a teleporting v-dialog) so the route guard's deferred
      navigation is resolved by buttons that stay within this component's
      tree. Opened by beforeRouteLeave when hasUnsavedChanges is true.
    -->
    <div
      v-if="unsavedDialog"
      class="sae-unsaved-scrim"
      data-testid="unsaved-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sae-unsaved-title"
    >
      <div class="sae-unsaved-card">
        <h2 id="sae-unsaved-title" class="sae-unsaved-title">You have unsaved changes</h2>
        <p class="sae-unsaved-body">
          Your most recent edits to this store have not finished saving.
          Leave anyway and risk losing them, or stay to let them save.
        </p>
        <div class="sae-unsaved-actions">
          <button
            type="button"
            class="sae-unsaved-btn sae-unsaved-btn--ghost"
            data-testid="unsaved-stay"
            @click="cancelLeaveUnsaved"
          >
            Stay on page
          </button>
          <button
            type="button"
            class="sae-unsaved-btn sae-unsaved-btn--danger"
            data-testid="unsaved-leave"
            @click="confirmLeaveUnsaved"
          >
            Leave without saving
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import moment, { min } from "moment";
import helpers from "@/mixins/helpers";
import _ from "lodash";
import { stripRelationshipFields, STORE_CREATE_ENDPOINT } from "@/utils/campaign";
import {
  classifyStoreAccount,
  findClientAdmin,
} from "./storeAccountRoles";
import { notifyStoreCreated } from "@/stores/sidebarRefresh";

import CampaignSetup from "./StoreSetup";
import CampaignClientContact from "./StoreClientContact";
import CampaignShopper from "./StoreShopper";
import CampaignFeatures from "./StoreFeatures";
import CampaignCheckoutInfo from "./StoreCheckoutInfo";
// PR-G (2026-06-22): the Checkout section is now an orchestrator that
// stacks the legacy CheckoutInfo cards alongside Shopper Allowance and
// Store Limits & Security (lifted in from Pricing).
import StoreCheckout from "./StoreCheckout.vue";
// PR E: the legacy ``StoreDepartments`` section (backed by the dropped
// ``stores.departments`` JSON column) was removed. Departments are now
// authored on the client page and mapped to the store via
// ``StoreDepartmentMapping`` (imported below).
import CampaignManagers from "./StoreManagers";
import CampaignProducts from "./StoreProducts";
import StoreBranding from "./StoreBranding.vue";
import CampaignDecorations from "./StoreDecorations";
import LinkBubble from "@/components/LinkBubble.vue";
import CampaignEditShell from "./StoreEditShell.vue";
import CampaignReviewLaunch from "./StoreReviewLaunch.vue";
import CampaignEmailConfig from "./StoreEmailConfig.vue";
import CampaignTaxLegal from "./StoreTaxLegal.vue";
import StoreDefaultsSection from "./StoreDefaultsSection.vue";
// C1 (2026-07-24, ADR 0059): Categories & Storefront page shell -- see
// docs/planning/2026-07-23-store-categories-implementation-plan.md.
import StoreCategories from "./StoreCategories/index.vue";
// Client Portal v2 PR B: store-level config for the new
// department-mapping + allowance-policy model. Self-contained cards
// (own GET/PUT endpoints), mounted in the Team & Departments section.
import StoreDepartmentMapping from "./StoreDepartmentMapping.vue";
import { useEnableDepartments } from "@/composables/useEnableDepartments";
import StoreAllowancePolicy from "./StoreAllowancePolicy.vue";
import StorePricingGrid from "./StorePricingGrid.vue";
import BatchesPanel from "./StoreBatches/BatchesPanel.vue";
import ArchivedStoreBanner from "./components/ArchivedStoreBanner.vue";
import {
  DEFAULT_RULES as DEFAULT_BATCH_RULES,
  money as batchMoney,
} from "@/services/batchManagementService";
import { computeDisableFlags } from "./disableFlags.js";
import { getActivePinia } from "pinia";
import { useClientsStore } from "@/stores/clients";
import { useDecorationRollupStore } from "@/stores/decorationRollup";
import { useStoreCategoryRollupStore } from "@/stores/storeCategoryRollup";
import { useStoreReadinessStore } from "@/stores/storeReadiness";
import { resolveClientContactChange } from "./clientContactSave.js";
import { newPermanentStoreWindow, reopenedCloseDate } from "./storeWindow.js";
import { isAccessRuleRefusal } from "@/utils/storeAccessRules";
import { isCheckoutSaveRefusal } from "@/utils/checkoutSaveRefusals";
import { shopperSaveRefusal } from "./shopperSaveRefusal.js";
import { unreadySections } from "./unreadySections.js";
import { reviewedNexusCount, nexusDistributorId } from "./nexusReviewed.js";
import { useAccountStore } from "@/stores/account";

// Autosave debounce. Settled-change window before the save fires.
// Started at 1500ms historically, tightened to 600ms (#1366), then to
// 350ms because the editor is mostly toggle / single-field edits and
// the longer window felt sluggish to users who toggled then quickly
// navigated. beforeRouteLeave also flushes a pending timer
// synchronously (see _flushAutoSaveNow) so even with this short
// window there is no race between debounce and nav.
const AUTOSAVE_DEBOUNCE_MS = 350;

// Safety valve (Bug Intake #445). Autosave fires on "the store changed", so
// anything that marks the store changed from inside its own save re-arms it,
// and the editor then saves forever (330 requests before a hard refresh).
// The loop's signature is a save that sends the SAME store again: the most
// autosaves in a row that leave the store identical, with no input from the
// owner in between. Past it the debounce stops and says so. Saves that each
// send something new (uploads finishing one after another) never count.
const AUTOSAVE_MAX_IDENTICAL_SAVES = 5;

// What counts as input from the owner. Listened for on the document, in the
// capture phase, so menus and dialogs Vuetify teleports out of the editor
// count too. Only TRUSTED events count (the browser's, from a person): code
// that dispatches one must not be able to reopen a stopped loop.
const OWNER_INPUT_EVENTS = ["input", "change", "keydown", "pointerdown", "paste", "drop"];

// The store's signature for the valve. Passwords are reduced to "set", so a
// typed password is never kept in it.
function autoSaveSignature(campaign) {
  try {
    return JSON.stringify(campaign, (key, value) =>
      key === "password" ? (value ? "<set>" : value) : value
    );
  } catch (err) {
    // Cannot happen for a store the save just serialized; if it does, every
    // save reads alike, so a loop is still caught (the leave save is not
    // metered, so nothing is lost).
    return "<unserializable>";
  }
}

// The Store Login rule slice: the settings the server judges together
// (services/store_access_rules) plus the tax/fee settings that choosing
// payroll clears in the browser (#351). A refused save puts exactly these
// back to the saved values -- nothing else the owner is editing.
const SAVED_ACCESS_FIELDS = [
  "access_mode",
  "storefront_password",
  "is_restrict_email_domain",
  "email_domains",
  "payment_method",
  "collect_sales_tax",
  "flat_rate",
  "collect_processing_fee",
  "consolidate_tax_fee",
];

// The Checkout section's slice: what the server refuses together when a
// store save cannot keep the checkout fields or ship-to (Bug Intake #450).
// A refused Checkout save puts exactly these back to the saved values.
// ``has_orders`` rides along so the section reads the same row it re-hydrates
// from.
const SAVED_CHECKOUT_FIELDS = [
  "checkout_fields",
  "dropdown_fields",
  "shipping_mode",
  "ship_to_department_id",
  "field_phone_enabled",
  "has_orders",
];

/**
 * The server's reason for REFUSING a store save (HTTP 400), or "" when the
 * error is anything else (network, 5xx). SAFRS wraps a ValidationError as
 * ``{errors: [{title, detail}]}``.
 */
function storeSaveRefusalMessage(err) {
  const response = err && err.response;
  if (!response || response.status !== 400) return "";
  const data = response.data || {};
  const first = Array.isArray(data.errors) ? data.errors[0] : null;
  const message =
    (first && (first.detail || first.title)) || data.error || data.message;
  return typeof message === "string" && message.trim()
    ? message
    : "This change could not be saved.";
}

export default {
  name: "Store",

  components: {
    "campaign-setup": CampaignSetup,
    "campaign-client-contact": CampaignClientContact,
    "campaign-shopper": CampaignShopper,
    "campaign-features": CampaignFeatures,
    "campaign-checkout-info": CampaignCheckoutInfo,
    "store-checkout": StoreCheckout,
    "campaign-managers": CampaignManagers,
    "campaign-products": CampaignProducts,
    "store-branding": StoreBranding,
    "campaign-decorations": CampaignDecorations,
    LinkBubble,
    "campaign-edit-shell": CampaignEditShell,
    "campaign-review-launch": CampaignReviewLaunch,
    "campaign-email-config": CampaignEmailConfig,
    "campaign-tax-legal": CampaignTaxLegal,
    "store-defaults-section": StoreDefaultsSection,
    "store-categories": StoreCategories,
    "store-department-mapping": StoreDepartmentMapping,
    "store-allowance-policy": StoreAllowancePolicy,
    "store-pricing-grid": StorePricingGrid,
    "batches-panel": BatchesPanel,
    "archived-store-banner": ArchivedStoreBanner,
  },
  setup() {
    // Departments feature gate (SA-global), read through the ONE shared
    // composable. Only the department-MAPPING card consumes it; the rest
    // of the store editor is untouched. Defaults TRUE so the card does
    // not flicker out while the GET is in flight.
    const { enableDepartments } = useEnableDepartments();
    return { enableDepartments };
  },
  mixins: [helpers],
  data() {
    return {
      // Bumped when the Shopper Allowance card saves, so the pricing grid
      // re-reads its platform-fee warning (decision #8).
      feeWarningRefreshKey: 0,
      // Whether the Owner confirmed the sales-tax states list (#502). The
      // Tax & Legal tile counts the states only once this is true.
      nexusConfirmed: false,
      campaignLoading: false,
      isMounted: false,
      isSaving: false,
      // The client_id persisted on the store when it was last loaded.
      // Used by the save path to detect a contact change and detach the
      // previously-attached client (legacy-client -> first-class-Client
      // migration, Phase 1).
      _persistedClientId: null,
      activeTab: 0,
      roles: {},
      campaign: {
        id: null,
        name: null,
        active: false,
        company_name: null,
        // New stores are Permanent (Jake, 2026-10-01): open now, with NO
        // close date (see storeWindow.js). Switching to Pop-Up later fills
        // a 7-day window.
        ...newPermanentStoreWindow(),
        // New stores are sell-enabled by default (2026-07-08): prices shown
        // to shoppers + individual shopper payments accepted. accept-payments
        // depends on storefront_pricing, so both default on together. The
        // builder turns them off for a catalog-only / allowance store.
        storefront_pricing: true,
        // New stores charge live-rate shipping by default (Jake, 2026-10-22);
        // the server applies the same default when the key is absent.
        shipping_cost_enabled: true,
        // Allowance v1->v2 (Slice 4): the legacy company_allowance /
        // company_allowance_personal_pay store columns were dropped. New
        // stores no longer seed them; shopper allowance is configured via the
        // v2 allowance-policy card, and cap-vs-hybrid derives from
        // payment_method.
        items_count_limit: 0,
        price_limit: 0,
        email_shopper: false,
        checkout_fields: [],
        departments: null,
        managers: null,
        accounts: null,
        message: null,
        bfl_cost: 0,
        distributor_po: null,
        pick_pack_partner_message: null,
        // pick_pack_partner_id removed (card https://trello.com/c/90bFtNjs).
        distributor: null,
        // distributor_id intentionally NOT initialized here. The
        // backend ``Store.__init__`` derives it from the request's
        // effective tenant (impersonation-aware), so the create body
        // does not need -- and must not -- carry a client-generated
        // value. Previously this field was initialized to 0 and never
        // updated, which (a) tripped a 500 ForeignKeyViolation under
        // true-SA-not-impersonating (the validator SA-bypass let the
        // sentinel through to INSERT) and (b) tripped a 400 under
        // Owner/Builder/SA-impersonating with an opaque "Check the
        // details" inline error. Audit finding F1
        // (docs/planning/2026-06-24-impersonation-correctness-audit-handoff.md).
        campaign_products: null,
        has_orders: null,
        _jv: {
          type: "Store",
        },
        tax_exempt: false,
        collect_processing_fee: false,
        consolidate_tax_fee: false,
        // New stores default to collecting sales tax (Jake-approved).
        // Existing stores load their saved value via loadCampaign() (which
        // replaces `campaign` wholesale), so a store saved OFF stays OFF.
        collect_sales_tax: true,
        complete: false,
        flat_rate: 0,
        fit_company_banner: false,
        dropdown_fields: [],
        is_low_stock: false,
        is_manually_closed: false,
        storefront_login_image: null,
        storefront_password: null,
        is_prevent_order_duplication: false,
        is_restrict_email_domain: false,
        email_domains: [],
        is_permanent_store: true,
        shopper_url: "",
      },

      departmentsEnabled: false,
      managersEnabled: false,

      isSaved: false,
      campaignSaved: true,

      exitWithoutSavingModal: false,

      // Dirty-form navigation guard (nested-route editor). The
      // beforeRouteLeave guard opens this modal when there are unsaved
      // changes and stashes the router `next` callback so the user's
      // Leave / Stay choice resolves the deferred navigation.
      unsavedDialog: false,
      _pendingLeaveNext: null,

      actionButtonsStatus: true,

      //check if Products and Campaign tabs are available
      isCampaignAvailable: false,

      maxVisitedTabId: 0,

      product_count: 0,
      productsLoaded: false,

      checkoutFieldsValid: false,
      tabsKey: 0,
      incompleteTabs: [],

      // The store's SAVED Store Login slice, as the server reports it
      // (GET /stores/<id>/access_state/) after load and after every save.
      // A refused save puts it back on ``campaign`` (Bug Intake #344).
      savedAccess: null,
      // The reason the last save was refused, shown inline; cleared by the
      // next successful save.
      saveRefusal: "",
      // The safety valve stopped autosave (#445); cleared by the next
      // successful save (after an owner edit, or the save on leaving).
      autoSaveHalted: false,

      // IMPORTANT:
      // Preserve the order of this list when changing tab order or adding/removing tabs
      tabNames: [
        "campaignSetup",
        "clientContact",
        "shopper",
        "features",
        "checkoutInfo",
        "departments",
        "locations",
        "managers",
        // 8: fulfillment — the Fulfillment section rendered an empty panel
        // after the pick/pack and Pickup Locations surfaces were removed, so
        // the whole section was dropped from the sidebar. The tabNames slot
        // is kept inert (like "locations"/index 6) to preserve positional
        // tab indexing for the section bridge; no section/ref/handlers exist.
        "fulfillment",
        "decorations",
        "products",
        "preview",
        "orders",
        "reports",
        // 14: emailConfig — extracted from CampaignFeatures into its
        // own Notifications sidebar section.
        "emailConfig",
      ],
      // since dictionaries are not necessarily ordered, the order of this does not need to be preserved
      tabKeys: [],
      visitedTabs: [],
      campaignSetupValid: null,

      initialLocations: [],
      invalidTabsKey: 0,

      mustVisitToValidate: ["departments", "managers"],
      subscriptionStatus: null,

      backgroundColor: "#ffffff",
      campaignActivated: false,

      usernameError: null,
      emailError: null,

      tabNotificationMessage: "To open store, fill in details below",
      savingProgress: 0,
    };
  },

  watch: {
    // Fetch the decoration rollup as soon as a persisted store id is known
    // (initial load or store switch) so the Decorations nav subtitle/status
    // are correct on editor load -- not only after the user opens the
    // Decorations section. Shares the useDecorationRollupStore Pinia store
    // with StoreDecorations.vue, so a fetch from either surface updates both
    // (the subtitle stays live as decorations change during the session).
    // The store is keyed by storeId and swallows its own errors, so this is
    // fire-and-forget.
    "campaign.id": {
      immediate: true,
      handler(newId) {
        if (newId == null || !this.rollupStore) return;
        this.rollupStore.fetchRollup(newId).catch(() => {
          // Non-fatal: the subtitle simply falls back to "No decorations"
          // (count 0) if the rollup can't be fetched. StoreDecorations.vue
          // owns the user-facing error/retry surface.
        });
        // Same pattern for the Categories & Storefront nav count: fetch
        // as soon as a persisted store id is known so the subtitle is
        // correct on editor load, not only after the section body's own
        // fetch resolves. StoreCategories/index.vue also republishes its
        // count via setCount() on every add/delete so both surfaces stay
        // live without a second network round-trip.
        if (this.categoryRollupStore) {
          this.categoryRollupStore.fetchCount(newId).catch(() => {
            // Non-fatal: the subtitle falls back to "No categories"
            // (count 0). StoreCategories/index.vue owns the user-facing
            // error/retry surface.
          });
        }
      },
    },
    activeTab: async function(newVal, oldVal) {
      // if Decorations tab is selected,
      // load existing decorations list to disable remove icons about them

      //if creating new campaign
      // show tab error after leaving an incomplete tab
      if (!this.editMode) {
        if (this.tabNames[oldVal] == "products") {
          await this.getProductCount();
          this.validateProductsTab();
        } else {
          if (this.canValidateTab(this.tabNames[oldVal])) {
            let invalid = this.safeValidateTab(this.tabNames[oldVal]);
            if (invalid !== undefined) this.updateTabInvalid(this.tabNames[oldVal], invalid);
          }
        }
      }

      if (newVal === 9) {
        const ref = this.tabRef(newVal);
        if (ref && typeof ref.loadExistingDecorations === "function") {
          ref.loadExistingDecorations();
        }
      }

      if (newVal == 4) {
        const ref = this.tabRef(newVal);
        if (ref && typeof ref.getUsedCustomDropdownOptions === "function") {
          ref.getUsedCustomDropdownOptions();
        }
      }

      if (this.editMode) {
        if (this.canValidateTab(this.tabNames[newVal])) {
          if (this.tabNames[newVal] == "products") {
            await this.getProductCount();
            this.validateProductsTab();
          } else {
            this.safeValidateTab(this.tabNames[newVal]);
          }
        }
      }
      //if creating
      if (this.tabNames[newVal] == "orders") {
        //add all tabs to visited tabs list
        this.tabNames.forEach((tabName) => {
          this.visitedTabs[this.tabNames.indexOf(tabName)] = true;
        });
        this.validateAllTabs();
      }
      //if creating new campaign
      //only show tab as invalid if they've already visited it
      //or the tab is checkoutfields and requires a location field
      else if (
        this.visitedTabs[newVal] ||
        (newVal == 4 &&
          (this.$store.getters.getSmartShippingEnabled ||
            this.$store.getters.getCollectSalesTaxEnabled))
      ) {
        if (this.tabNames[newVal] == "products") {
          this.validateProductsTab();
        } else {
          this.safeValidateTab(this.tabNames[newVal]);
        }
      }
    },
  },

  computed: {
    // True when the editor holds changes not yet persisted. Reads the
    // autosave state machine ('dirty' = settled-but-unflushed, 'saving' =
    // in flight, 'error' = last save failed) plus a still-pending debounce
    // timer (a change happened but the 1.5s window hasn't elapsed). 'saved'
    // with no pending timer means everything is on disk -- safe to leave.
    hasUnsavedChanges() {
      const autoSaveState =
        this.$store?.state?.storeEntity?.autoSaveState || "saved";
      if (["dirty", "saving", "error"].includes(autoSaveState)) return true;
      return !!this._autoSaveTimer;
    },

    editMode() {
      this.$store.commit("setActionButtonsStatus", true);
      this.$store.commit("setDisableNextButton", false);
      this.$store.commit("setBFLCostChanged", false);
      this.$store.commit("setIsClickedExitButton", false);

      return this.$route.params.id ? true : false;
    },

    // Disable-flag bundle used by sections that need to lock controls
    // based on campaign lifecycle (complete / active / has-orders /
    // newly-placed-order). Mirrors what CampaignFeatures.disableFlags
    // computes via the shared computeDisableFlags helper.
    disableFlags() {
      return computeDisableFlags({
        campaign: this.campaign,
        isNewOrderAdded: this.$store.getters.getIsNewOrderAdded,
      });
    },

    // Shared decoration-rollup Pinia store -- the SINGLE source of truth
    // for this store's decorations. StoreDecorations.vue (the Decorations
    // section body) reads the same store, so the nav subtitle/status below
    // and the section list can never disagree. Replaces the eternally-empty
    // legacy `campaign.decoration_list` JSONList column the decoration
    // refactor abandoned (nothing populates it anymore, so the old subtitle
    // always fell to "No decorations").
    //
    // Guarded on getActivePinia(): in the running app Pinia is always
    // installed, but StoreAddEdit is mounted in many unit tests without a
    // Pinia instance, and resolving the store then throws "no active
    // Pinia". Returning null in that case degrades gracefully (count 0)
    // instead of crashing every computed that touches the store.
    rollupStore() {
      if (!getActivePinia()) return null;
      return useDecorationRollupStore();
    },

    // Distinct decoration count for this store, read from the rollup and
    // filtered to EXACTLY what the Decorations section counts: `archived`
    // decorations are excluded (StoreDecorations.vue's `decorations`
    // computed drops them per ADR 0056), so the nav count matches the
    // section list. null store id / not-yet-fetched rollup -> 0.
    decorationCount() {
      const storeId = this.campaign?.id;
      if (storeId == null || !this.rollupStore) return 0;
      const rollup = this.rollupStore.rollupFor(storeId);
      const list = Array.isArray(rollup?.decorations) ? rollup.decorations : [];
      return list.filter((d) => d.lifecycle_state !== "archived").length;
    },

    // Shared category-rollup Pinia store (mirrors rollupStore/
    // decorationCount above) -- the SINGLE source of truth for this
    // store's Categories & Storefront nav count. StoreCategories/
    // index.vue (the section body) publishes into the same store on
    // fetch/add/delete, so the nav subtitle and the section's own
    // categoryCountLabel can never disagree.
    //
    // Same getActivePinia() guard as rollupStore: StoreAddEdit mounts in
    // many unit tests without a Pinia instance.
    categoryRollupStore() {
      if (!getActivePinia()) return null;
      return useStoreCategoryRollupStore();
    },

    // Category count for this store, read from the shared rollup. null
    // store id / not-yet-fetched rollup -> 0 (subtitle falls back to
    // "No categories").
    categoryCount() {
      const storeId = this.campaign?.id;
      if (storeId == null || !this.categoryRollupStore) return 0;
      const count = this.categoryRollupStore.countFor(storeId);
      return typeof count === "number" ? count : 0;
    },

    // Section status, derived from campaign data — NOT from the
    // legacy incompleteTabs validation map.
    //
    // Why not validation: the Vuetify v-form.validate() that backs
    // each section's validateTab() is async, while validateAllTabs()
    // calls it synchronously and reads a stale `this.valid` data
    // property. Several sections also gate validation behind
    // mustVisitToValidate (departments/locations/managers) so they
    // only validate after the user clicks in. Net effect: sections
    // showed "Not Started" on the Review cards until the user
    // navigated into them once, even on a fully-configured campaign.
    //
    // The data-driven approach reads the same campaign fields that
    // sectionSubtitle reads, so the two can never disagree. Done if
    // the section's primary data is present; warn if a clearly-required
    // field is missing on a campaign that's far enough along to need
    // it; null otherwise.
    //
    // The legacy incompleteTabs map is still relevant for the SAVE
    // path (saveAndExitCampaign uses it to gate which tabs get a
    // `false` invalid_tabs entry on save), so we don't remove it.
    // Whether the store's shared password is STORED, per the server
    // (``has_store_password``). Until the first read lands, fall back to the
    // mode copy so a loading editor does not flash a warning.
    storeHasPassword() {
      if (this.savedAccess) return !!this.savedAccess.has_store_password;
      return !!this.campaign?.storefront_password;
    },

    // The editor tabs the SERVER says keep this store from opening -- the
    // same live answer the Stores list shows (Bug Intake #355). The freshest
    // copy wins: the readiness endpoint's, which the editor re-reads after
    // every save (``_refreshServerReadiness``); before that, the one
    // getCampaignById returned on load.
    serverFlaggedTabs() {
      const storeId = this.campaign?.id ?? this.campaign?._jv?.id;
      if (storeId != null && getActivePinia()) {
        const fresh = useStoreReadinessStore().readinessFor(storeId);
        if (fresh && Array.isArray(fresh.unready_tabs)) return fresh.unready_tabs;
      }
      const tabs = this.campaign?.unready_tabs;
      return Array.isArray(tabs) ? tabs : [];
    },

    sectionStatus() {
      const c = this.campaign || {};
      const dist = c.distributor || {};
      const out = {};

      // Section ids are the canonical nested-route ids (see
      // sectionRoutes.js SECTION_IDS), so the shell's sidebar lookups
      // (sectionStatus[sec.id]) resolve. The Round-2 consolidations fold
      // multiple legacy data signals into a single status: client-store
      // combines name + client; team combines managers + departments.

      // A store "has a client" when its first-class ``client_id`` FK is
      // set (legacy-client -> first-class-Client migration, Phase 1).
      // The old role_id=4 ("Client Admin") account is no longer the
      // contact carrier.
      const hasClient = c.client_id != null;

      // client-store - merges name (store name + dates) and client.
      // Done when the store is named, dated, AND has a client; warn when
      // named but missing client or dates; null when unnamed.
      const nameComplete = !!(c.name && c.start_date && (c.end_date || c.is_permanent_store));
      if (nameComplete && hasClient) {
        out["client-store"] = "done";
      } else if (c.name) {
        out["client-store"] = "warn";
      } else {
        out["client-store"] = null;
      }

      // defaults - Phase 4.11 (route fork) Store Defaults section. Its
      // one remaining knob (default_decoration_set_id; default_colors was
      // removed 2026-08-13, default_margin_percent 2026-09-29) is optional
      // -- a store with no default set is valid. So this section never
      // requires input to be launch-ready: it is
      // "done" whenever the store exists. Without this branch the key
      // was undefined, so doneCount could never reach SECTION_META.length
      // and Launch was permanently disabled for EVERY store.
      out.defaults = "done";

      // categories - C1 (2026-07-24, ADR 0059). The Categories &
      // Storefront page has no required input yet (every store gets an
      // auto-created Home Page category and, for new stores, 7 defaults
      // -- see A1), and per the ADR 0059 implementation plan's open
      // questions, a hidden category has no Review & Launch readiness
      // treatment "for now" -- the section is invisible to the launch
      // gate. Always "done" so it never blocks Launch, mirroring
      // `defaults` above.
      out.categories = "done";

      // batches - ADR 0066 (2026-08-20). The Batches section has NO
      // required input: a store's batching rules all fall back to the
      // platform defaults (48h window, $201 threshold, manual release,
      // shopper editing off -- design doc 3.2/3.3, where a NULL column
      // means "inherit"), and the batches themselves are created by the
      // store when a shopper orders, never by the distributor. So this
      // section is "done" whenever the store exists, mirroring `defaults`
      // and `categories` above. Without this key doneCount could never
      // reach SECTION_META.length and Launch would be permanently
      // disabled for EVERY store -- the exact regression the `defaults`
      // comment above records.
      out.batches = "done";

      // branding — brand color OR brand image. G1 keeps the existing
      // "any branding asset = done" heuristic; carousel slide counts
      // live on a separate endpoint (StoreBranding loads them locally)
      // so we can't gate on them from the parent without an extra fetch.
      out.branding = (c.brand_color || c.brand_image) ? "done" : null;

      // store-login - reads ``access_mode`` (PR C1). "open" is always
      // done; "password" / "email_domain" / "invite_codes" require their
      // mode-specific config to be present. When access_mode is null
      // (pre-C1 rows) the row defaults to "open" via the read-path
      // helper, so we treat it as done.
      //
      // Launch gate (fix/launch-gate-requires-slug): a store has no shopper
      // URL until its canonical ``slug`` (Custom Link) is set. slug is
      // deliberately NULLABLE so a draft store can exist without one, but a
      // store with no slug is NOT launchable -- there is nowhere for
      // shoppers to reach it. So an empty/blank slug forces "warn"
      // regardless of access_mode, which drops store-login out of doneCount
      // and pushes StoreReviewLaunch's warnCount above zero, blocking
      // canLaunch and surfacing the section in the preflight list. This is a
      // presentational launch-gate check only: the visible Custom Link field
      // still validates empty as OK (see validateURL / StoreShopper), so a
      // draft with no slug still SAVES without a field error.
      const accessMode = c.access_mode;
      const hasSlug = !!(c.slug && String(c.slug).trim());
      if (!hasSlug) {
        out["store-login"] = "warn";
      } else if (!accessMode || accessMode === "open") {
        out["store-login"] = "done";
      } else if (accessMode === "password") {
        // Done only when a store password is actually STORED (#342, R5):
        // ``storefront_password`` is just a copy of the mode.
        out["store-login"] = this.storeHasPassword ? "done" : "warn";
      } else if (accessMode === "email_domain") {
        // ``email_domains`` is the canonical array column (see
        // StoreAccessControl.vue). Legacy fallbacks cover ad-hoc
        // shapes seen during the C1 rollout window.
        const domains = Array.isArray(c.email_domains) ? c.email_domains : null;
        const hasDomain =
          (domains && domains.length > 0) ||
          !!c.restrict_email_domain_value ||
          !!c.email_domain ||
          !!c.email_domain_restriction;
        out["store-login"] = hasDomain ? "done" : "warn";
      } else if (accessMode === "invite_codes") {
        out["store-login"] = "done";
      } else {
        out["store-login"] = "done";
      }

      // checkout - at least one checkout field selected (PR-G rename from
      // checkout-fields).
      out["checkout"] = (Array.isArray(c.checkout_fields) && c.checkout_fields.length) ? "done" : null;

      // products — has any
      const productCount = typeof this.product_count === "number"
        ? this.product_count
        : (Array.isArray(c.campaign_products) ? c.campaign_products.length : 0);
      out.products = productCount > 0 ? "done" : null;

      // decorations - read from the shared decoration_rollup source, NOT
      // the dead decoration_list column.
      //
      // The store's DECORATOR choice deliberately does not move this.
      // "Assign automatically" (an explicit backend value; a legacy null
      // reads the same -- Jake, 2026-09-29) and a tenant id forcing one
      // decorator are both complete choices. From
      // 2026-08-24 to 2026-09-29 a missing decorator made this "warn",
      // which StoreReviewLaunch's warnCount turns into a disabled Launch
      // button -- re-adding it would block a store the backend accepts.
      out.decorations = this.decorationCount > 0 ? "done" : null;

      // pricing - payment method set OR explicit storefront pricing toggle
      // (PR-G rename from pricing-checkout).
      if (c.payment_method || c.storefront_pricing) {
        out["pricing"] = "done";
      } else {
        out["pricing"] = null;
      }

      // tax-legal - exempt OR collecting OR distributor has nexus configured
      const nexusCount = reviewedNexusCount(dist, this.nexusConfirmed);
      if (c.tax_exempt || c.collect_sales_tax || nexusCount > 0) {
        out["tax-legal"] = "done";
      } else {
        out["tax-legal"] = null;
      }

      // notifications — PR F1/F3 introduced the notification_preferences
      // matrix + identity fields (from-name, reply-to, branded-email).
      // "done" means the user has expressed intent: any matrix cell is
      // true OR any identity field has a non-default value. The legacy
      // ``email_shopper`` flag stays a positive signal for old rows that
      // haven't migrated yet. A fully default store is null (untouched).
      const prefs = c.notification_preferences;
      let anyMatrixCell = false;
      if (prefs && typeof prefs === "object") {
        for (const eventKey of Object.keys(prefs)) {
          const row = prefs[eventKey];
          if (row && typeof row === "object") {
            for (const recipKey of Object.keys(row)) {
              if (row[recipKey] === true) {
                anyMatrixCell = true;
                break;
              }
            }
          }
          if (anyMatrixCell) break;
        }
      }
      const anyIdentity =
        !!c.notification_from_name ||
        !!c.notification_reply_to_email ||
        c.notification_branded_email === true;
      if (anyMatrixCell || anyIdentity || c.email_shopper) {
        out.notifications = "done";
      } else {
        out.notifications = null;
      }

      // team - merges team members + departments. Done when either has any.
      // PR E: departments come from the typed client-department mapping
      // (``client_departments``), not the dropped legacy JSON column.
      //
      // Issue #2815: this counted ``c.managers`` -- the LEGACY JSON column.
      // The Team card provisions real Accounts (account_stores) and never
      // writes that column, so inviting people never flipped Team to done;
      // only a mapped department could. ``team_member_count`` is the real
      // roster count from the server. ``c.managers`` stays in the OR as a
      // fallback: the column is still writable and older stores carry rows.
      const hasTeamMembers =
        Number(c.team_member_count) > 0 ||
        (Array.isArray(c.managers) && c.managers.length > 0);
      const hasDepartments =
        Array.isArray(c.client_departments) && c.client_departments.length;
      out.team = (hasTeamMembers || hasDepartments) ? "done" : null;

      // Promote any legacy invalid-tab signal into 'warn'. If the user
      // has visited a section and it failed validation, surface that.
      // Legacy tab name -> new section id (consolidations applied: the
      // setup/buyer tabs both feed client-store; managers + departments
      // both feed team).
      const tabToSection = {
        campaignSetup: "client-store",
        clientContact: "client-store",
        preview: "branding",
        shopper: "store-login",
        checkoutInfo: "checkout",
        departments: "team",
        products: "products",
        decorations: "decorations",
        features: "pricing",
        emailConfig: "notifications",
        managers: "team",
      };
      for (const [tn, secId] of Object.entries(tabToSection)) {
        if (this.incompleteTabs && this.incompleteTabs[tn] === true) {
          out[secId] = "warn";
        }
      }

      // The SERVER's own verdict (Bug Backlog #355). The Stores list hides
      // the Open/Closed toggle for a store whose live readiness
      // (``unready_tabs``) flags a tab, so Review & Launch must not call that
      // store ready: a flagged section reads warn here, with its "Fix in ..."
      // link. A local re-check does not override it -- the server judges
      // things a section validator cannot see (a resolvable ship-to, a
      // stored password) -- the post-save re-read clears it instead.
      for (const tn of this.serverFlaggedTabs) {
        for (const section of unreadySections([tn])) {
          out[section.id] = "warn";
        }
      }

      return out;
    },

    // sectionId -> short summary string shown in sidebar under the title.
    // Reads the live campaign for human-meaningful state. Every section
    // gets a subtitle when the data exists; fall back to a default
    // descriptor when the field is empty so the sidebar doesn't have
    // visually inconsistent rows (some with subtitles, some without).
    sectionSubtitle() {
      const c = this.campaign || {};
      const dist = c.distributor || {};
      const out = {};

      // client-store - merges Name & Launch + Client Info. Prefer the
      // store/company name; append the buyer name when known.
      // By slug, not by id: Client Admin is 4 only on a seeded database
      // (it is 11 on production), so the old ``role_id === 4`` never
      // matched there and the buyer name was silently dropped from the
      // sidebar label (#3579).
      const buyer = findClientAdmin(c.accounts);
      const buyerName = buyer
        ? ([buyer.first_name, buyer.last_name].filter(Boolean).join(' ') || buyer.email)
        : '';
      if (c.name) {
        const companyLabel = c.effective_company_name || c.company_name;
        const storeLabel = companyLabel ? `${companyLabel} · ${c.name}` : c.name;
        out['client-store'] = buyerName ? `${storeLabel} · ${buyerName}` : storeLabel;
      } else if (buyerName) {
        out['client-store'] = buyerName;
      } else {
        out['client-store'] = dist.name ? `Distributor: ${dist.name}` : 'Not configured';
      }

      // Branding — brand color + banner-set hint
      if (c.brand_color) {
        out.branding = c.brand_image ? `${c.brand_color} · banner set` : `Brand color ${c.brand_color}`;
      } else if (c.brand_image) {
        out.branding = 'Banner set';
      } else {
        out.branding = 'No branding configured';
      }

      // store-login - read access_mode (PR C1) for the canonical state.
      // Each mode shows a different summary: open == public, password
      // == "Password protected", email_domain == the configured domain,
      // invite_codes == "Invite codes". Pre-C1 rows have no access_mode;
      // fall back to the legacy signals (storefront_password / slug).
      const accessModeForSub = c.access_mode;
      if (accessModeForSub === 'open') {
        out['store-login'] = 'Open storefront';
      } else if (accessModeForSub === 'password') {
        out['store-login'] = this.storeHasPassword ? 'Password protected' : 'Password protected (no password set)';
      } else if (accessModeForSub === 'email_domain') {
        // ``email_domains`` is an array column (canonical, per
        // StoreAccessControl.vue). Render a count + the first domain
        // as a hint. Legacy single-value fallbacks cover transitional
        // shapes from the C1 rollout window.
        const domains = Array.isArray(c.email_domains) ? c.email_domains : null;
        if (domains && domains.length > 0) {
          const first = domains[0];
          out['store-login'] = domains.length === 1
            ? `Email domain: ${first}`
            : `Email domain: ${first} +${domains.length - 1}`;
        } else {
          const dom = c.restrict_email_domain_value || c.email_domain || c.email_domain_restriction;
          out['store-login'] = dom ? `Email domain: ${dom}` : 'Email domain (none set)';
        }
      } else if (accessModeForSub === 'invite_codes') {
        out['store-login'] = 'Invite codes';
      } else if (c.slug) {
        // slug-unify Phase 4 PR-3 (#2286): canonical identity is ``slug``.
        out['store-login'] = `URL · ${c.slug}`;
      } else if (c.storefront_password) {
        out['store-login'] = 'Password protected';
      } else {
        out['store-login'] = 'Public storefront';
      }

      // checkout - count of selected fields (PR-G rename from checkout-fields)
      if (Array.isArray(c.checkout_fields) && c.checkout_fields.length) {
        out['checkout'] = `${c.checkout_fields.length} field${c.checkout_fields.length === 1 ? '' : 's'}`;
      } else {
        out['checkout'] = 'Not configured';
      }

      if (typeof this.product_count === 'number' && this.product_count > 0) {
        out.products = `${this.product_count} product${this.product_count === 1 ? '' : 's'}`;
      } else if (Array.isArray(c.campaign_products) && c.campaign_products.length) {
        out.products = `${c.campaign_products.length} product${c.campaign_products.length === 1 ? '' : 's'}`;
      } else {
        out.products = 'No products';
      }

      // decorations - count of DISTINCT decorations from the shared
      // decoration_rollup (StoreDecorations.vue reads the same source), so
      // the nav count agrees with the section list. The legacy
      // decoration_list column is dead (never populated post-refactor) and
      // is deliberately NOT read here.
      const decoCount = this.decorationCount;
      if (decoCount > 0) {
        out.decorations = `${decoCount} design${decoCount === 1 ? '' : 's'}`;
      } else {
        out.decorations = 'No decorations';
      }

      // categories - count of categories from the shared
      // storeCategoryRollup Pinia store (mirrors the decorations subtitle
      // above). StoreCategories/index.vue (the section body) publishes
      // its own already-loaded count into the same store on fetch/add/
      // delete, so the nav count and the section's categoryCountLabel
      // can never disagree, and the nav updates reactively without a
      // page reload.
      const catCount = this.categoryCount;
      if (catCount > 0) {
        out.categories = `${catCount} categor${catCount === 1 ? 'y' : 'ies'}`;
      } else {
        out.categories = 'No categories';
      }

      // batches - ADR 0066 (2026-08-20). The card summarises the store's
      // governing batching rules in the same one-line shape the section's
      // own collapsed panel uses ("48h window - $201.00 threshold -
      // manual release"), because the rules are what explain the list.
      // Both columns are nullable-means-inherit (design doc 3.2), so a
      // NULL reads as the platform default rather than as "unset" -- the
      // rule really is in force, it just is not overridden here.
      const holdHours = c.batch_hold_hours != null
        ? c.batch_hold_hours
        : DEFAULT_BATCH_RULES.windowHours;
      const thresholdDollars = c.batch_blanks_threshold_cents != null
        ? Math.round(c.batch_blanks_threshold_cents) / 100
        : DEFAULT_BATCH_RULES.threshold;
      out.batches = `${holdHours}h window · ${batchMoney(thresholdDollars)} threshold`;

      // pricing - payment method (PR-G rename from pricing-checkout).
      // Allowance v1->v2 (Slice 4): the legacy company_allowance column was
      // dropped, so the flat "$X credit" hint is retired. Shopper allowance
      // now lives on the v2 allowance-policy card.
      if (c.payment_method) {
        out['pricing'] = `${c.payment_method}`;
      } else if (c.storefront_pricing) {
        out['pricing'] = 'Storefront pricing on';
      } else {
        out['pricing'] = 'Not configured';
      }

      // tax-legal - nexus count + collect/exempt summary
      const nexus = reviewedNexusCount(dist, this.nexusConfirmed);
      if (c.tax_exempt) {
        out['tax-legal'] = 'Tax exempt';
      } else if (c.collect_sales_tax) {
        out['tax-legal'] = nexus ? `Collecting · ${nexus} state${nexus === 1 ? '' : 's'} nexus` : 'Collecting tax';
      } else if (nexus) {
        out['tax-legal'] = `${nexus} state${nexus === 1 ? '' : 's'} nexus configured`;
      } else {
        out['tax-legal'] = 'No tax configured';
      }

      // notifications - count configured event/recipient cells, with
      // a branded-emails hint when on. Falls back to the legacy
      // email_shopper toggle for pre-F1 rows.
      const subPrefs = c.notification_preferences;
      let cellCount = 0;
      if (subPrefs && typeof subPrefs === 'object') {
        for (const eventKey of Object.keys(subPrefs)) {
          const row = subPrefs[eventKey];
          if (row && typeof row === 'object') {
            for (const recipKey of Object.keys(row)) {
              if (row[recipKey] === true) cellCount += 1;
            }
          }
        }
      }
      const brandedHint = c.notification_branded_email ? ' · branded emails on' : '';
      if (cellCount > 0) {
        out.notifications = `${cellCount} notification${cellCount === 1 ? '' : 's'} on${brandedHint}`;
      } else if (c.notification_from_name || c.notification_reply_to_email || c.notification_branded_email) {
        out.notifications = `Identity configured${brandedHint}`;
      } else if (c.email_shopper) {
        out.notifications = 'Email confirmation on';
      } else {
        out.notifications = 'No notifications configured';
      }

      // team - merges managers + departments counts into one summary line.
      // PR E: department count comes from the typed client-department mapping.
      // Issue #2815: prefer the real roster count; fall back to the legacy
      // JSON length so stores that still carry those rows read correctly.
      const legacyMgrCount = Array.isArray(c.managers) ? c.managers.length : 0;
      const mgrCount = Number(c.team_member_count) > 0
        ? Number(c.team_member_count)
        : legacyMgrCount;
      const deptCount = Array.isArray(c.client_departments)
        ? c.client_departments.length
        : 0;
      if (mgrCount || deptCount) {
        const parts = [];
        parts.push(`${mgrCount} manager${mgrCount === 1 ? '' : 's'}`);
        parts.push(`${deptCount} department${deptCount === 1 ? '' : 's'}`);
        out.team = parts.join(' · ');
      } else {
        out.team = 'No team configured';
      }

      return out;
    },

    availableTabs() {
      //watch incompleteTabs for changes
      if (this.invalidTabsKey);
      let tabList = [0];

      if (this.isCampaignAvailable) {
        tabList.push(1, 2, 3, 4);
      }

      // these tabs can be enabled in the features tab.
      // (tab index 6 / "locations" was the legacy Pickup Locations surface,
      // removed from the editor UI; the tabNames slot is kept inert to
      // preserve positional tab indexing for the section bridge.)
      if (this.departmentsEnabled) {
        tabList.push(5);
      }

      if (this.managersEnabled) {
        tabList.push(7);
      }

      // decorations is only available if first tab is complete
      // Products is only available if first tab is complete
      // preview is only available if first tab is complete
      // notifications (emailConfig — extracted from features for the
      // redesign) is also gated on isCampaignAvailable.
      // (tab index 8 / "fulfillment" was dropped from the sidebar; the slot
      // is pushed here only to preserve positional indexing — tabRef(8) is
      // always undefined, so the save loops' missing-ref guard skips it.)
      if (this.isCampaignAvailable) {
        tabList.push(8);
        tabList.push(9);
        tabList.push(10);
        tabList.push(11);
        tabList.push(14); // notifications / emailConfig
      }

      // orders and reports are only available if there are products
      if (
        this.product_count > 0 &&
        !this.incompleteTabs["clientContact"] &&
        !this.incompleteTabs["shopper"]
      ) {
        tabList.push(12);
        tabList.push(13);
      }

      return tabList;
    },

    formsAreValid() {
      // check if forms are valid, up until the currently active tab
      let tabsUntilActive = this.availableTabs.slice(
        0,
        this.availableTabs.indexOf(this.activeTab) + 1
      );
      for (const tabNumber in tabsUntilActive) {
        if (this.tabRef(tabNumber) && !this.tabRef(tabNumber).valid) {
          return false;
        }
      }

      return $store.getters.getShippingFormValid;
    },

    nextButtonDisabled() {
      if (this.activeTab === Math.max(...this.availableTabs)) {
        return true;
      }

      return false;
    },

    backButtonDisabled() {
      if (this.activeTab === 0) {
        return true;
      }

      return false;
    },

    backgroundColorStyle() {
      return "background-color: " + this.backgroundColor + ";";
    },

    campaignSwitchDisabled() {
      let disabled = false;

      if (
        this.$store.state.account &&
        this.$store.state.account.role.name == "Client Admin"
      ) {
        disabled = true;
      }

      return disabled;
    },
  },

  async mounted() {
    let response = await this.$axios.get(`/subscription/getSubscriptionStatus`);
    this.subscriptionStatus = response.data;
    if (
      this.subscriptionStatus == "suspended" ||
      this.subscriptionStatus == "canceled"
    ) {
      this.$router.push("/distributor/stores");
    }
    this.$store.commit("setProductsLoaded", false);
    this.$store.commit("setNewOrderAdded", false);
    this.$store.commit("setSmartShippingEnabled", false);
    this.$store.commit("setProductEditModalPopped", false);
    this.$store.commit("setFieldCustomEnabled", false);
    this.$store.commit("setRemoveVariant", false);
    this.$store.commit("setShopperURLChanged", false);
    this.$store.commit("setShopperAccountChanged", false);
    this.$store.commit("setBFLCostChanged", false);

    await this.loadRoles();

    if (this.editMode) {
      await this.loadCampaign();
      this.campaignSaved = true;
      this.maxVisitedTabId = 13;
      this.loadNexusConfirmation();
    }

    // allow the destination tab to be specified in the query string
    if (this.$route.query.tab) {
      this.activeTab = parseInt(this.$route.query.tab);
    }

    this.$data.isMounted = true;
    this.syncCampaignActivated();

    // Owner input resets the autosave safety valve (#445). Not after a
    // teardown that raced the awaits above: nothing would remove it.
    if (!this._destroyed) {
      this._ownerInputListener = (event) => {
        if (event && event.isTrusted) this._onOwnerInput();
      };
      for (const type of OWNER_INPUT_EVENTS) {
        document.addEventListener(type, this._ownerInputListener, true);
      }
    }

    // Wait one tick for the shell to render its initial section slot
    // before kicking off edit-mode validation, which walks tabRef(i)
    // for each available tab.
    //
    // Section components are mounted lazily by CampaignEditShell — each
    // section content is rendered via a dynamic slot keyed on
    // `activeSectionId`. Previously this hook eagerly collated every
    // `this.$refs.<sectionName>` into a `tabRefs` snapshot, but at this
    // point only the default section's slot is in the DOM, so all the
    // other entries were captured as `undefined` and never refreshed.
    // First-navigation events (like clicking the Products card)
    // dereferenced the stale undefined and silently no-op'd.
    //
    // tabRef(idx) replaces the cache with a live $refs lookup, so
    // wherever this component asks "what's the child for tab i?", the
    // answer always reflects what's currently mounted.
    this.$nextTick(() => {
      if (this.editMode) {
        this.isSaved = true;
        this.validateAllTabs();
      }
    });
  },

  // Cancel a pending autosave debounce + mark the instance as torn down
  // so any in-flight _runAutoSave short-circuits on its next checkpoint.
  // Prevents the 1.5s setTimeout from firing on a destroyed VM (zombie
  // PATCH against a campaign the user has navigated away from).
  beforeUnmount() {
    this._destroyed = true;
    if (this._autoSaveTimer) {
      clearTimeout(this._autoSaveTimer);
      this._autoSaveTimer = null;
    }
    this._autoSavePending = false;
    if (this._ownerInputListener) {
      for (const type of OWNER_INPUT_EVENTS) {
        document.removeEventListener(type, this._ownerInputListener, true);
      }
      this._ownerInputListener = null;
    }
    // The top bar shows the edited store name on ANY distributor page
    // while it is set (useTopBar), so leaving the editor clears it.
    this.$store.commit("setEditStoreName", "");
  },

  // Unsaved-changes navigation guard. With a single `:section?` param, a
  // section-to-section move stays on the same route record, so this guard
  // only fires when LEAVING the editor entirely -- exactly when an
  // unsaved-changes prompt is warranted. When there are no pending changes
  // the navigation proceeds immediately; otherwise the modal opens and the
  // router `next` callback is stashed until the user chooses Leave / Stay.
  beforeRouteLeave(to, from, next) {
    if (!this.hasUnsavedChanges) {
      next();
      return;
    }
    // If the only thing keeping the editor "dirty" is a settled change
    // waiting on the debounce timer or already marked dirty but not yet
    // in flight, flush it synchronously instead of dialog-prompting.
    // Only the "saving" (in flight, can't double-fire) and "error" (last
    // save failed, user must decide what to do) cases need the dialog.
    const autoSaveState =
      this.$store?.state?.storeEntity?.autoSaveState || "saved";
    // A HALTED autosave (#445) is flushed too: the valve stops only the
    // debounce-driven loop, never the owner's save on the way out.
    const canFlush =
      autoSaveState === "dirty" ||
      this.autoSaveHalted ||
      (!!this._autoSaveTimer && autoSaveState === "saved");
    if (canFlush) {
      this._flushAutoSaveNow()
        .catch(() => {
          // Save failed -- fall back to the dialog so the user sees
          // the error state before leaving.
          this._pendingLeaveNext = next;
          this.unsavedDialog = true;
        })
        .then(() => {
          // Only proceed with nav if the dialog did NOT just open.
          if (!this.unsavedDialog) next();
        });
      return;
    }
    this._pendingLeaveNext = next;
    this.unsavedDialog = true;
  },

  methods: {
    // Bug Intake #502: read the Owner's confirmation of the sales-tax
    // states so the Tax & Legal tile does not count the auto-added signup
    // state as done. Best effort: unknown means not confirmed.
    async loadNexusConfirmation() {
      const id = nexusDistributorId(
        this.campaign,
        getActivePinia() ? useAccountStore().distributor?.id : null
      );
      if (!id) return;
      try {
        const resp = await this.$axios.get(`/distributor/nexusConfirmation/${id}`);
        this.nexusConfirmed = !!resp?.data?.confirmed_at;
      } catch (e) {
        this.nexusConfirmed = false;
      }
    },
    // allow editing only Orders and Reports when the campaign is visited when edit mode is disabled.
    syncCampaignActivated() {
      if (
        this.campaignSwitchDisabled ||
        this.subscriptionStatus == "suspended" ||
        this.subscriptionStatus == "canceled" ||
        this.campaign.complete
      ) {
        this.campaignActivated = true;
        // this.nextTab(12);
      } else {
        this.campaignActivated = false;
      }
    },

    // The banner's Unarchive worked: reload the store (now unarchived) so the
    // shell drops its restricted Batches-only mode and the full editor returns.
    async onUnarchived() {
      await this.loadCampaign();
      this.syncCampaignActivated();
    },

    onAllowanceSaved() {
      this.feeWarningRefreshKey += 1;
      // The payment summary quotes the allowance (card #666).
      this.$refs.features?.refreshAllowancePolicy?.();
    },

    // Unsaved-changes modal: user chose "Leave" -- resolve the deferred
    // navigation and dismiss the modal.
    confirmLeaveUnsaved() {
      const next = this._pendingLeaveNext;
      this._pendingLeaveNext = null;
      this.unsavedDialog = false;
      if (next) next();
    },

    // Unsaved-changes modal: user chose "Stay" -- cancel the deferred
    // navigation (next(false)) and dismiss the modal.
    cancelLeaveUnsaved() {
      const next = this._pendingLeaveNext;
      this._pendingLeaveNext = null;
      this.unsavedDialog = false;
      if (next) next(false);
    },

    // Live $refs lookup for a tab/section's child component, keyed by
    // the legacy tab index.
    //
    // CampaignEditShell mounts section content via a dynamic slot
    // keyed on activeSectionId — children for non-active sections are
    // NOT mounted until the user navigates to them. Any cached map of
    // refs collated at parent-mount time would only see the default
    // section's child and capture every other entry as undefined.
    //
    // tabRef(idx) re-reads $refs on each call so callers always get
    // the currently-mounted component (or undefined if that section
    // has never been visited).
    tabRef(idx) {
      const name = this.tabNames[idx];
      return name ? this.$refs[name] : undefined;
    },

    // Safely invoke a section ref's validateTab() method, guarding both
    // the ref-is-mounted check (optional chaining) AND the
    // method-exists check (typeof === "function").
    //
    // Why both: post-rearch, some section components (notably the new
    // Notifications matrix surface in StoreEmailConfig.vue) no longer
    // expose a validateTab method. When the lazy slot has mounted the
    // section, $refs[name] is a real component instance -- optional
    // chaining no longer short-circuits -- but the missing method
    // raises TypeError on every tab navigation. Returns undefined when
    // the call is skipped so callers can distinguish "no result" from
    // "validation returned undefined" the same way they did before.
    safeValidateTab(name) {
      const ref = this.$refs[name];
      if (ref && typeof ref.validateTab === "function") {
        return ref.validateTab();
      }
      return undefined;
    },

    // Re-read the server's live readiness verdict after a save, so Review &
    // Launch and the sidebar say what the Stores list says (Bug Intake
    // #355). Fire-and-forget: a failed read leaves the previous verdict in
    // place and must not turn a successful save into a failure.
    _refreshServerReadiness() {
      const storeId = this.campaign?._jv?.id ?? this.campaign?.id;
      if (storeId == null || !getActivePinia()) return;
      useStoreReadinessStore()
        .fetchReadiness(storeId)
        .catch((err) => {
          console.warn("[CampaignAddEdit] readiness re-read failed", describeError(err));
        });
    },

    async toggleStoreActive(newValue) {
      try {
        // Re-opening a Pop-Up whose close has passed moves the close a
        // week out, exactly as the Stores list toggle does
        // (reopenedCloseDate is the one rule for every re-activate path);
        // otherwise the store would "launch" and still read closed. A
        // store with no close date sends "null", which the server reads
        // as "leave the close date alone".
        const endDate = (newValue ? reopenedCloseDate(this.campaign) : this.campaign.end_date) || null;
        const isManualClose = !newValue;
        await this.$axios.get(
          `/campaign/activeCampaign/${this.campaign._jv.id}/${newValue}/${endDate}/${isManualClose}`
        );
        // Hold what the server just persisted, so the editor (status pill,
        // a later save) does not keep the pre-launch state.
        this.campaign.active = newValue;
        this.campaign.is_manually_closed = isManualClose;
        if (endDate !== null) this.campaign.end_date = endDate;
        this.$store.dispatch(
          newValue ? "raiseSuccess" : "raiseInfo",
          newValue ? "Store is now open" : "Store is now closed"
        );
      } catch (err) {
        this.campaign.active = !newValue;
        this.$store.dispatch("raiseError", err);
      }
      // Either way the server's can-launch answer may have moved (now
      // live, or a new refusal reason): Review & Launch reads it from
      // the shared readiness store.
      this._refreshServerReadiness();
    },

    routeToStore() {
      // After first save of a new campaign, route directly to the
      // canonical nested editor on the section the user just saved
      // (Setup -> client-store). Avoids round-tripping through the
      // legacy /distributor/campaigns/:id redirect and prevents the
      // dirty-guard modal from firing mid-save when autoSaveState is
      // still 'dirty' or 'saving'.
      this.$router.push(
        "/distributor/stores/" + this.campaign._jv.id + "/edit/client-store"
      );
    },

    setBackgroundColor(color) {
      this.backgroundColor = color;
    },

    // ------------------------------------------------------------------
    // Claude Design redesign — section-id ↔ legacy tab-index bridge.
    //
    // The new shell uses string section ids ("name", "client", "branding"
    // etc.) and hash routing. The legacy script still reasons in numeric
    // `activeTab` indices (the `watch: activeTab` block triggers
    // validation, decoration loading, etc.). These methods keep both
    // models in sync so the existing script logic works unchanged.
    // ------------------------------------------------------------------

    // sectionId -> legacy tabName (the FIRST tabName when a nested-route
    // section maps to multiple legacy tabs). Section ids are the canonical
    // nested-route ids (see sectionRoutes.js SECTION_IDS); the Round-2
    // consolidations map merged sections to their FIRST legacy tab:
    // client-store -> campaignSetup (Name & Launch), team -> managers.
    _sectionIdToTabName(sectionId) {
      return {
        review: null, // synthetic, no underlying tab
        "client-store": "campaignSetup",
        branding: "preview",
        "store-login": "shopper",
        // Checkout (PR-G rename from checkout-fields) -- now an orchestrator
        // (StoreCheckout) wrapping Identity / Custom fields / Shipping (the
        // legacy CampaignCheckoutInfo content) plus Shopper Allowance and
        // Store Limits & Security cards lifted in from Pricing. The ref on
        // the section root is still ``checkoutInfo`` so the legacy
        // activeTab + ref lookups continue to work without churn.
        "checkout": "checkoutInfo",
        // Defaults (Phase 4.11) -- net-new section with no legacy
        // tab. Returns null like ``review`` so the legacy activeTab
        // side-effects (validation, decoration loading, etc.) do not
        // fire when the user navigates here. The section persists via
        // its own GET/PATCH so it does not need to participate in the
        // parent's autosave/validation cycle.
        defaults: null,
        products: "products",
        decorations: "decorations",
        // Categories & Storefront (C1, 2026-07-24) -- net-new section
        // with no legacy tab, same rationale as ``defaults`` above: it
        // owns its own GET (and, from C2 on, PATCH) against
        // /api/stores/<id>/categories, so it does not participate in
        // the legacy activeTab side-effects or the tabNames index bridge.
        categories: null,
        // Batches (ADR 0066, 2026-08-20) -- net-new section with no legacy
        // tab, same rationale as ``defaults`` and ``categories`` above. It
        // owns its own calls against /batch-management/stores/<id>/... and
        // has no form state to validate or autosave: every mutation is an
        // explicit confirmed action, and the batches themselves are created
        // by the store rather than by this editor. Listed explicitly rather
        // than left to fall through to undefined, so the intent is recorded.
        batches: null,
        // Pricing (PR-G rename from pricing-checkout).
        "pricing": "features",
        // Tax & Legal renders CampaignTaxLegal (nexus read-only + tax
        // toggles + legal placeholder). No dedicated legacy tab; reuse
        // "checkoutInfo" so the legacy activeTab watcher's side-effects
        // don't fire incorrectly.
        "tax-legal": "checkoutInfo",
        notifications: "emailConfig",
        // Team & Departments merges managers + departments; map to the
        // FIRST legacy tab (managers).
        team: "managers",
      }[sectionId];
    },

    // Convert a section id to the legacy numeric tab index used by
    // the existing watch and ref system.
    _sectionIdToTabIndex(sectionId) {
      const tn = this._sectionIdToTabName(sectionId);
      if (!tn) return -1;
      return this.tabNames.indexOf(tn);
    },

    // Programmatically navigate to a section. Pushes the nested editor
    // route; the shell derives its active section from the route param.
    // The Review and Launch landing ('review') is the bare /edit URL.
    //
    // New-store guard (#1827): on the /stores/add flow the campaign has
    // no id yet, so a nested URL would carry `null`/`undefined`. Route
    // through createThenNavigate so the store is created first and the
    // real id lands in the URL.
    goToSection(sectionId) {
      if (!this.campaign?.id) {
        this.createThenNavigate(sectionId);
        return;
      }
      const base = `/distributor/stores/${this.campaign.id}/edit`;
      this.$router.push(
        sectionId === "review" ? base : `${base}/${sectionId}`
      );
    },

    // Create-first navigation for a brand-new store (#1827).
    //
    // The redesigned StoreEditShell navigates by route change only; it no
    // longer drives the legacy nextTab() create path. On /stores/add the
    // campaign has no id, so nothing fired the initial POST -- the URL
    // became /stores/null/edit/..., autosave never armed, and edits were
    // lost. This handler owns the orphaned create logic: it persists the
    // store once (saveCampaign, stay-on-page), captures the server id,
    // then routes to the section the user asked for on the real id.
    //
    // Guarded so rapid double navigation (two sidebar clicks before the
    // POST resolves) fires exactly one create. Concurrent callers await
    // the same in-flight promise, then route.
    async createThenNavigate(sectionId) {
      // Already persisted -> plain navigation, no create.
      if (this.campaign && this.campaign.id) {
        this.goToSection(sectionId);
        return;
      }
      if (this._createInFlight) {
        // A create is already running; wait for it, then route. Don't
        // start a second POST.
        try {
          await this._createInFlight;
        } catch (err) {
          return; // create failed; error already surfaced by saveCampaign
        }
        if (this.campaign && this.campaign.id) {
          this.goToSection(sectionId);
        }
        return;
      }
      this._createInFlight = (async () => {
        await this.saveTab();
        // saveCampaign(false): create + stay on page (no redirect to the
        // campaigns list). Populates campaign._jv.id / campaign.id.
        await this.saveCampaign(false);
      })();
      try {
        await this._createInFlight;
      } catch (err) {
        // saveCampaign already dispatched the error toast; abort nav.
        return;
      } finally {
        this._createInFlight = null;
      }
      const newId =
        (this.campaign && this.campaign.id) ||
        (this.campaign && this.campaign._jv && this.campaign._jv.id);
      if (!newId) return; // create did not yield an id; stay put
      const base = `/distributor/stores/${newId}/edit`;
      this.$router.push(
        sectionId === "review" ? base : `${base}/${sectionId}`
      );
    },

    // Shell emits this when the user clicks a sidebar item or hash
    // changes. Sync legacy `activeTab` so the existing watcher fires
    // its side-effects (validation triggers etc.) AND fire the
    // tab-specific load methods that the legacy nextTab/prevTab calls
    // would have triggered. The activeTab watcher alone doesn't load
    // products, decorations, etc. — only nextTab/prevTab do — so we
    // re-run the relevant subset here.
    onActiveSectionChanged(sectionId) {
      const idx = this._sectionIdToTabIndex(sectionId);
      if (idx >= 0 && idx !== this.activeTab) {
        this.activeTab = idx;
      }
      // Trigger tab-specific lazy loads. Mirrors the loadComponent /
      // loadCampaignProducts / loadExistingDecorations calls from
      // nextTab() at lines ~1923-1950 of this file.
      this.$nextTick(() => {
        const ref = this.tabRef(this.activeTab);
        if (!ref) return;
        if (typeof ref.loadComponent === "function") {
          ref.loadComponent();
        }
        if (this.activeTab === 10 && typeof ref.loadCampaignProducts === "function") {
          // Products tab — load the campaign's products so cards render.
          ref.loadCampaignProducts();
        }
        if (this.activeTab === 9 && typeof ref.loadExistingDecorations === "function") {
          ref.loadExistingDecorations();
        }
        if (this.activeTab === 4 && typeof ref.getUsedCustomDropdownOptions === "function") {
          ref.getUsedCustomDropdownOptions();
        }
      });
    },

    // Fired after the shell successfully reverted via the backend
    // edit-session. Reload the campaign data so the live form reflects
    // the restored state.
    async onRevertCompleted() {
      try {
        // Force a refetch — uses the same load path as initial mount.
        if (this.campaign && this.campaign.id) {
          const id = this.campaign.id;
          await this.$store.dispatch(
            "jv/getRelated",
            `Campaign/${id}?include=campaign_products,locations`
          );
          // Re-pull campaign root attrs into our local state.
          const fresh = this.$store.getters["jv/get"](`Campaign/${id}`);
          if (fresh) {
            // Only copy attributes; preserve our reactive scaffolding.
            for (const key of Object.keys(fresh)) {
              if (key === "_jv") continue;
              this.campaign[key] = fresh[key];
            }
          }
          // Visual hint that revert worked.
          this.$store.dispatch("raiseSuccess", "Changes discarded. Store restored to its original state.");
        }
      } catch (err) {
        console.warn("[CampaignAddEdit] reload after revert failed", describeError(err));
      }
    },

    canValidateTab(tabName) {
      const tabIndex = this.tabNames.indexOf(tabName);
      return (
        !this.mustVisitToValidate.includes(tabName) ||
        this.visitedTabs[tabIndex] ||
        this.activeTab === tabIndex
      );
    },

    async validateAllTabs() {
      this.tabNames.forEach((tabName) => {
        // Section refs that no longer expose validateTab (e.g., the
        // notifications matrix in StoreEmailConfig.vue) are skipped --
        // the typeof check inside safeValidateTab guards the missing
        // method. Sections never visited (no ref yet) are also skipped.
        const ref = this.$refs[tabName];
        if (!ref || typeof ref.validateTab !== "function") return;
        if (tabName == "departments") {
          if (this.departmentsEnabled) {
            const invalid = this.safeValidateTab(tabName);
            this.incompleteTabs[tabName] = invalid;
          } else {
            this.incompleteTabs[tabName] = false;
          }
        } else if (tabName == "managers") {
          if (this.managersEnabled) {
            const invalid = this.safeValidateTab(tabName);
            this.incompleteTabs[tabName] = invalid;
          } else {
            this.incompleteTabs[tabName] = false;
          }
        } else if (this.canValidateTab(tabName)) {
          const invalid = this.safeValidateTab(tabName);
          this.incompleteTabs[tabName] = invalid;
        }
      });
      if (this.canValidateTab("products")) {
        await this.validateProductsTab();
      }
    },

    async validateProductsTab() {
      let invalid;
      if (this.product_count == 0) {
        this.updateTabInvalid("products", true);
        invalid = true;
      } else if (this.$store.getters.getSmartShippingEnabled) {
        let productsIncomplete = await this.$refs[
          "products"
        ].getIsProductsIncomplete();
        if (productsIncomplete) {
          this.updateTabInvalid("products", true);
          invalid = true;
        } else {
          this.updateTabInvalid("products", false);
          invalid = false;
        }
        this.$forceUpdate();
      } else {
        this.updateTabInvalid("products", false);
        invalid = false;
        this.$forceUpdate();
      }
      this.safeValidateTab("products");
      return !invalid;
    },

    async campaignChanged(event) {
      this.isSaved = false;
      if (event == "shipping_cost") {
        await this.validateProductsTab();
      }
      // Claude Design redesign — kick the autosave debounce.
      // Only autosave when we're editing an existing campaign (id is
      // set) and we've completed initial load. Skips during creation
      // flow to avoid PATCHing an in-flight new-campaign POST.
      if (this.editMode && this.campaign?.id && this.isMounted) {
        this.$store.commit("setAutoSaveState", "dirty");
        this._kickAutoSave();
      }
    },

    // Debounced autosave. Reuses the existing saveAndExitCampaign which
    // iterates each tab's updateCampaign(). Heavy (full-tree save), but
    // correct — no risk of partial-save drift since the same path the
    // legacy "Exit & Save" used now fires on every settled change.
    _kickAutoSave() {
      if (this._autoSaveTimer) clearTimeout(this._autoSaveTimer);
      this._autoSaveTimer = setTimeout(() => {
        this._autoSaveTimer = null;
        this._autoSaveFromDebounce();
      }, AUTOSAVE_DEBOUNCE_MS);
    },

    // The debounce's save, behind the safety valve (#445). Only saves the
    // debounce starts are counted: an explicit flush (leaving the editor)
    // is one save the owner asked for and always runs.
    _autoSaveFromDebounce() {
      if (this._destroyed) return;
      if (this._autoSaveInFlight) {
        // Not a new save yet: the in-flight one re-arms the debounce when
        // it finishes, and that run is the one counted.
        this._autoSavePending = true;
        return;
      }
      if ((this._identicalAutoSaves || 0) >= AUTOSAVE_MAX_IDENTICAL_SAVES) {
        this._haltAutoSave();
        return;
      }
      this._runAutoSave();
    },

    // After every save attempt (good, refused or failed, debounce or
    // flush): did it leave the store exactly as the previous one did? A run
    // of those with no owner input between is the loop's signature.
    _noteAutoSaveResult() {
      const signature = autoSaveSignature(this.campaign);
      this._identicalAutoSaves =
        signature === this._lastAutoSaveSignature
          ? (this._identicalAutoSaves || 0) + 1
          : 1;
      this._lastAutoSaveSignature = signature;
    },

    // Stop: nothing is left armed, the save dot shows the error state, and
    // the banner says why. The owner's next edit resets the count.
    _haltAutoSave() {
      this._autoSavePending = false;
      this.autoSaveHalted = true;
      this.$store.commit("setAutoSaveState", "error");
      console.warn(
        "[CampaignAddEdit] autosave stopped: " +
          AUTOSAVE_MAX_IDENTICAL_SAVES +
          " identical saves in a row with no owner input"
      );
    },

    _onOwnerInput() {
      this._identicalAutoSaves = 0;
      this._lastAutoSaveSignature = null;
    },

    // Cancel any pending debounce + fire the save immediately. Returns
    // a Promise that resolves after the save completes (success or
    // failure). Used by beforeRouteLeave so a pending autosave that
    // hasn't kicked yet flushes before nav rather than dialog-prompting
    // the user about "unsaved changes."
    _flushAutoSaveNow() {
      if (this._autoSaveTimer) {
        clearTimeout(this._autoSaveTimer);
        this._autoSaveTimer = null;
      }
      return this._runAutoSave();
    },

    async _runAutoSave() {
      // Short-circuit if the component is being torn down (beforeUnmount
      // raced the debounce). Prevents touching $store / $refs on a
      // destroyed VM.
      if (this._destroyed) return;
      if (this._autoSaveInFlight) {
        this._autoSavePending = true;
        return;
      }
      this._autoSaveInFlight = true;
      this.$store.commit("setAutoSaveState", "saving");
      try {
        // Iterate each visited tab's updateCampaign(), then call
        // saveCampaign(false) to commit. Mirrors saveAndExitCampaign
        // but with closeCampaignEdit=false so the user stays on the
        // page (the legacy method redirects to /distributor/campaigns on
        // success, which is wrong for autosave).
        let invalid_tabs = this.incompleteTabs;
        if (this.product_count > 0) invalid_tabs["products"] = false;
        else invalid_tabs["products"] = true;

        for (let i = 0; i < this.availableTabs.length; i++) {
          const tabRef = this.tabRef(this.availableTabs[i]);
          if (!tabRef || !tabRef.updateCampaign) continue;
          invalid_tabs[this.tabNames[this.availableTabs[i]]] = false;
          try {
            const result = await tabRef.updateCampaign(this.campaign);
            this.campaign = result;
            if (result && result.error) {
              throw result.error;
            }
          } catch (err) {
            invalid_tabs[this.tabNames[this.availableTabs[i]]] = true;
            // Don't navigate or raise toast — autosave is silent. The
            // sidebar's status indicator (warn) will surface the issue.
            throw err;
          }
        }

        this.campaign.invalid_tabs = Object.assign({}, invalid_tabs);
        const continueSaving = await this.saveTab();
        if (continueSaving) {
          this._shopperLoginFailure = null;
          await this.saveCampaign(false); // false = stay on page
          this.saveRefusal = "";
          this._retriedAfterRefusal = false;
          await this.loadSavedAccess();
          this._refreshServerReadiness();
        }
        this.autoSaveHalted = false;
        this.$store.commit("setAutoSaveState", "saved");
      } catch (err) {
        console.warn("[CampaignAddEdit] autosave failed", describeError(err));
        this.$store.commit("setAutoSaveState", "error");
        if (this._shopperLoginFailure) {
          // The row saved; the store login did not, so a switch to Shared
          // password was held back (gate round 1, B5). Put the saved Store
          // Login state back and try once more. A refusal is already in
          // Store Login's inline alert; only a network/5xx failure, which
          // has no owner-facing text, goes to the banner.
          await this.revertToSavedAccess(this._shopperLoginFailure.banner);
        } else if (err && err.storeSaveRefused) {
          if (isAccessRuleRefusal(err.message)) {
            // A Store Login rule (or a refused domain): the saved slice
            // comes back and the rest of the edit is re-sent once.
            await this.revertToSavedAccess(err.message);
          } else if (isCheckoutSaveRefusal(err.message)) {
            // The Checkout slice (checkout fields, ship-to): same rule --
            // the saved slice comes back, said inline, rest re-sent once.
            await this.revertToSavedCheckout(err.message);
          } else {
            // Any other refusal is said inline too; the value stays on
            // screen for the owner to correct.
            this.saveRefusal = err.message;
          }
        }
      } finally {
        this._autoSaveInFlight = false;
        if (!this._destroyed) this._noteAutoSaveResult();
        // Don't re-arm a fresh debounce timer if the component has been
        // torn down while we were awaiting an HTTP round-trip.
        // beforeUnmount cleared _autoSaveTimer; we must not create a
        // replacement timer that would outlive the lifecycle contract
        // (even though the re-entry would no-op via the _destroyed
        // guard, the trailing setTimeout still escapes teardown).
        if (this._autoSavePending && !this._destroyed) {
          this._autoSavePending = false;
          this._kickAutoSave();
        }
      }
    },

    setDepartmentsEnabled(val) {
      this.departmentsEnabled = val;
      if (this.visitedTabs[this.tabNames.indexOf("departments")]) {
        this.safeValidateTab("departments");
      }
    },

    validateCheckoutFields(val) {
      this.checkoutFieldsValid = val;
    },

    setManagersEnabled(val) {
      this.managersEnabled = val;
      if (this.visitedTabs[this.tabNames.indexOf("managers")]) {
        this.safeValidateTab("managers");
      }
    },

    async changeCampaignProducts(campaignProductsList) {
      this.campaign.campaign_products = campaignProductsList;
      this.product_count = this.campaign.campaign_products.length;
      this.campaignChanged();
      await this.validateProductsTab();
      this.$forceUpdate();
    },

    setActionButtonsStatus(isVisible) {
      this.$data.actionButtonsStatus = isVisible;
    },

    setCampaignSaved() {
      this.campaignSaved = true;
    },

    moveCampaignTab(tabId) {
      this.activeTab = tabId;
    },

    async saveAndExitCampaign() {
      let invalid_tabs = this.incompleteTabs;
      if (this.product_count > 0) {
        invalid_tabs["products"] = false;
      } else {
        invalid_tabs["products"] = true;
      }

      let result;
      for (let i = 0; i < this.availableTabs.length; i++) {
        // Vuetify 3 lazy v-window-item: unvisited tabs aren't mounted,
        // so their refs are undefined. Skip them — they have no unsaved data.
        const tabRef = this.tabRef(this.availableTabs[i]);
        if (!tabRef || !tabRef.updateCampaign) {
          continue;
        }
        invalid_tabs[this.tabNames[this.availableTabs[i]]] = false;
        try {
          result = await tabRef.updateCampaign(this.campaign);
          this.campaign = result;
        } catch (err) {
          this.$store.dispatch("raiseError", err);
          console.log(describeError(err));
          invalid_tabs[this.tabNames[this.availableTabs[i]]] = true;
          this.exitWithoutSavingModal = false;
          // Navigate to the tab that failed so the user can fix it
          this.activeTab = this.availableTabs[i];
          return;
        }
        if (result && result.error) {
          this.$store.dispatch("raiseError", result.error);
          console.log(result.error);
          invalid_tabs[this.tabNames[this.availableTabs[i]]] = true;
          this.exitWithoutSavingModal = false;
          // Navigate to the tab with the error
          this.activeTab = this.availableTabs[i];
          return;
        }
      }

      this.campaign.invalid_tabs = Object.assign({}, invalid_tabs);
      let continueSaving;
      try {
        continueSaving = await this.saveTab();
        if (continueSaving) {
          await this.saveCampaign(true);
        }
      } catch (err) {
        this.$store.dispatch("raiseError", err);
      }

      this.exitWithoutSavingModal = false;
      // this.$store.commit("setEditCampaignName", "");
    },

    incrementSaveProgress(amount) {
      this.savingProgress += amount;
    },

    async saveCampaign(closeCampaignEdit = true) {
      // Prevent saving completed campaigns — setup fields are read-only
      if (this.campaign.complete) {
        if (closeCampaignEdit) {
          this.$router.push("/distributor/stores");
        }
        return;
      }

      this.isSaving = true;

      let newCampaign = _.cloneDeep(this.campaign);
      if (newCampaign.decoration_list == null) {
        newCampaign.decoration_list = [];
      }
      // New-store autosave default (card https://trello.com/c/kHeP97T7).
      // The editor autosaves on section-nav; a brand-new store starts with
      // name = null, which the backend name validator rejects (400
      // "Campaign name is not valid") -- so every section click failed and
      // store creation was blocked. Default a NEW store (no persisted id)
      // to "New Store" so the first create carries a valid, non-blank name;
      // autosave then works normally and the user renames anytime via the
      // name field. CREATE-only: an existing store (has _jv.id) is never
      // overwritten, and a name the user has already typed is preserved.
      const isNewStore = !(newCampaign._jv && newCampaign._jv.id);
      if (isNewStore && (!newCampaign.name || !newCampaign.name.trim())) {
        newCampaign.name = "New Store";
        // Mirror onto the reactive component data so the name field and
        // any downstream reads (e.g. the create POST built from
        // this.campaign below) see the default, not the stale null.
        this.campaign.name = "New Store";
      }
      // go to Shopper Login tab if shopper url is already exist under the same distributor
      // slug-unify Phase 4 (#2286): the canonical identity is ``slug``.
      // The Custom Link tab (StoreShopper) mirrors the freshly typed value
      // into ``slug``, and the legacy ``shopper_url`` column has been
      // dropped, so read ``slug`` directly. The existence-check endpoint
      // matches its path segment against ``slug`` on the backend.
      const customLinkForCheck = newCampaign.slug;
      if (
        customLinkForCheck &&
        customLinkForCheck.split(" ").join("") &&
        this.$store.getters.getShopperUrlChanged
      ) {
        let shopperUrl = customLinkForCheck
          .split(" ")
          .join("")
          .toLowerCase();
        let campaign_id =
          newCampaign._jv && newCampaign._jv.id ? newCampaign._jv.id : 0;
        // On CREATE, ``newCampaign.distributor_id`` is now undefined
        // (it was previously initialized to 0; the backend now derives
        // the tenant on insert). The collision check is only meaningful
        // for UPDATE (the tenant is known via the loaded row); skip
        // the cross-tenant collision pre-check during CREATE rather
        // than send ``undefined`` in the URL.
        const distributorIdForUrlCheck = newCampaign.distributor_id;
        let result = distributorIdForUrlCheck
          ? await this.$axios.get(
              `/campaign/checkExistingShopperURLByDistributor/${campaign_id}/${distributorIdForUrlCheck}/${shopperUrl}`
            )
          : { data: { count: 0 } };
        if (result.data.count > 0) {
          this.nextTab(2);
          /*
          this.$store.dispatch(
            "raiseError",
            "Store shopper url already exists"
          );
          */
          return;
        }
      }

      this.incrementSaveProgress(2);

      // pick_pack_partner_id auto-populate removed (card https://trello.com/c/90bFtNjs).
      // The getDefaultPickPack endpoint is gone; the column is being dropped.
      this.incrementSaveProgress(2);

      // save every tab one at a time, starting with the beginning. if any of the tabs fail to
      // save, switch focus to them and run the validator to point out any errors.
      if (!newCampaign._jv || !newCampaign._jv.id) {
        try {
          // Strip relationship fields before POST — see utils/campaign.js (#307, #308)
          const campaignToPost = stripRelationshipFields(newCampaign);
          // A store cannot START in Shared password mode: it has no shopper
          // login (no password) until after it exists, and the server
          // refuses that state on create (services/store_access_rules). It
          // is created open, and the mode is re-applied below for the
          // update_campaign that follows its password.
          const modeAfterCreate =
            campaignToPost.access_mode === "password" ? "password" : null;
          if (modeAfterCreate) {
            campaignToPost.access_mode = "open";
            campaignToPost.storefront_password = false;
            campaignToPost.is_restrict_email_domain = false;
          }
          // Client linkage is set ONLY via the guarded attach endpoint
          // (POST /clients/<id>/stores), NEVER via the SAFRS campaign
          // create — which has no allowlist scrub and would write
          // stores.client_id directly, bypassing the tenant guard. See
          // store.py's note that client_id is omitted from the
          // update_campaign RPC allowlist for the same reason. We keep
          // newCampaign.client_id (the local object) intact so the attach
          // block downstream still sees the selected id.
          delete campaignToPost.client_id;

          // POST to the real /stores collection (STORE_CREATE_ENDPOINT).
          // /campaigns 308-redirects and browsers refuse to follow a
          // redirect on the CORS preflight -> store-create died (#1868,
          // regression from #1831). The created object's _jv.id / .id
          // downstream reads are unchanged: /stores is the same underlying
          // SAFRS model (Campaign was renamed to Store), so the JSON:API
          // response shape is identical.
          let data = await this.$store.dispatch("jv/post", [
            campaignToPost,
            { url: STORE_CREATE_ENDPOINT },
          ]);
          // Preserve the tab data (accounts, locations, etc.) that
          // updateCampaign() merged into newCampaign. loadCampaign()
          // overwrites this.campaign with the server response which
          // has empty relationships — we must not lose the local data.
          const savedAccounts = newCampaign.accounts;
          const savedLocations = newCampaign.locations;
          // PR E: legacy ``departments`` JSON column dropped; departments are
          // mapped via the store-department-mapping card, not this row.
          const savedManagers = newCampaign.managers;
          // The reloaded server row has client_id = null (the create no
          // longer writes it), and the merge below overwrites newCampaign
          // from that row. Capture the SELECTED client id so we can
          // re-apply it after the merge — otherwise the attach block at
          // the bottom of saveCampaign sees null and never fires.
          const savedClientId = newCampaign.client_id;

          // Set the _jv.id so the update_campaign call works
          newCampaign._jv = data._jv;
          newCampaign.id = data._jv.id;

          // The create succeeded and a new store row now exists, so the
          // distributor sidebar's STORES count ("N stores . M live")
          // changed -- announce it so the sidebar refetches. This fires
          // ONLY here, in the create branch after a successful jv/post;
          // the edit/update_campaign path below does not change the COUNT
          // (same store) and so emits nothing. Interim: stores has no Pinia
          // store; the emit is routed through the notifyStoreCreated seam.
          // Full store-ification: https://trello.com/c/g9K52FsA.
          notifyStoreCreated();

          // Reload campaign to pick up server-generated fields (timestamps, defaults)
          await this.loadCampaign(data._jv.id);

          // Merge: start from server response, overlay the local tab data
          newCampaign = _.cloneDeep(this.campaign);
          if (savedAccounts && savedAccounts.length > 0) {
            newCampaign.accounts = savedAccounts;
          }
          if (savedLocations && savedLocations.length > 0) {
            newCampaign.locations = savedLocations;
          }
          if (savedManagers && savedManagers.length > 0) {
            newCampaign.managers = savedManagers;
          }
          // Restore the selected client id clobbered by the merge above
          // (the reloaded row's client_id is null since the create no
          // longer writes the FK). The attach block below reads this.
          newCampaign.client_id = savedClientId;
          if (modeAfterCreate) {
            newCampaign.access_mode = modeAfterCreate;
            newCampaign.storefront_password = true;
            newCampaign.is_restrict_email_domain = false;
          }
        } catch (err) {
          this.$store.dispatch("raiseError", err);
          console.log(describeError(err));
          this.isSaving = false;
          this.exitWithoutSavingModal = false;
          return;
        }
      }
      this.incrementSaveProgress(2);

      try {
        if (this.activeTab === 0 && !this.editMode) {
          newCampaign.campaign_products = [];
        }

        // update or create related account models
        let accountValues = [];
        if (newCampaign.accounts && newCampaign.accounts.length > 0) {
          newCampaign.accounts.forEach((account) => {
            let accountData = {
              username: account.username,
              email: account.email,
              first_name: account.first_name,
              last_name: account.last_name,
              password: account.password,
              active: account.active,
              reports_enabled: account.reports_enabled,
              role_id: account.role_id,
              // The role SLUG must be copied across too, or the dispatch
              // loop below has nothing stable to classify on and falls
              // back to treating every account as ordinary. ``role_slug``
              // is read-only server-side (a SAFRS jsonapi_attr with no
              // setter), so sending it back on the PATCH is a no-op
              // rather than a write.
              role_slug: account.role_slug,
              distributor_id: newCampaign.distributor_id,
              _jv: account._jv,
            };
            accountValues.push(accountData);
          });
        }
        // The shared store password is written BEFORE the store row: the
        // server refuses to save a store as "Shared password" until it
        // HAS a password (services/store_access_rules, Bug Intake #346), so
        // the password the owner just typed must already be stored when
        // update_campaign switches the mode. A null password (keep the
        // stored one) only provisions the store's shopper login.
        //
        // Sent whatever the rest of Store Login holds (an empty or taken
        // Custom link included): the login does not depend on the link, and
        // holding a typed password back while still sending the mode made
        // the server refuse the mode for want of the password just typed
        // (gate round 1, B1). A FAILED login save does not block the rest
        // of the store either (B5): the row still saves, minus a switch to
        // Shared password that needed this password, and the autosave says
        // why the password was not saved.
        const shopperForSave = accountValues.find(
          (a) => classifyStoreAccount(a) === "shopper"
        );
        // Only a login there is something to say about: one with a username,
        // or one carrying a password the owner typed. A blank-username
        // login with no password is not sent (it would be refused on every
        // autosave and read as "the store password could not be saved").
        const shopperHasSomethingToSave =
          !!shopperForSave &&
          ((typeof shopperForSave.username === "string" &&
            shopperForSave.username.trim() !== "") ||
            (typeof shopperForSave.password === "string" &&
              shopperForSave.password !== ""));
        // A refusal (a 400, or a 200 + error: a password that is too short,
        // the placeholder, a login clash -- #4356's shopperSaveRefusal) is
        // shown in Store Login's own inline alert and thrown at the END of
        // the save, after the row has saved. A network or 5xx failure has
        // no owner-facing text; it fails the save as itself and the editor
        // banner says the password was not saved.
        let shopperRefusal = null;
        let shopperError = null;
        if (newCampaign._jv && newCampaign._jv.id && shopperHasSomethingToSave) {
          let response = null;
          try {
            response = await this.$axios.post("/campaign/updateShopper", {
              username: shopperForSave.username,
              password: shopperForSave.password,
              distributor_id: shopperForSave.distributor_id,
              campaign_id: newCampaign._jv.id,
            });
          } catch (err) {
            console.warn(
              "[CampaignAddEdit] store login save failed",
              describeError(err)
            );
            if (shopperSaveRefusal(err && err.response)) {
              response = err.response;
            } else {
              shopperError = err;
            }
          }
          if (response) shopperRefusal = shopperSaveRefusal(response);
          // ONE place decides where a store-login failure is shown: Store
          // Login's own inline alert when that section is on screen (#4356),
          // otherwise the editor-wide banner. A network/5xx failure has no
          // owner-facing server text, so it always gets the banner's fixed
          // message.
          const loginSection = this.tabRef(this.tabNames.indexOf("shopper"));
          const inline = !!(loginSection && loginSection.showSaveError);
          if (inline) loginSection.showSaveError(shopperRefusal);
          // Accepted: the typed password is saved, so it must not ride along
          // with every later autosave (#496).
          if (
            response &&
            !shopperRefusal &&
            !shopperError &&
            typeof shopperForSave.password === "string" &&
            shopperForSave.password !== "" &&
            loginSection &&
            typeof loginSection.markPasswordSaved === "function"
          ) {
            loginSection.markPasswordSaved(shopperForSave.password);
          }
          if (shopperRefusal || shopperError) {
            this._shopperLoginFailure = {
              banner: shopperError
                ? "The store password could not be saved (the connection " +
                  "failed or the server had an error). Shared password is " +
                  "not turned on until it is."
                : inline
                  ? null
                  : shopperRefusal,
            };
            const saved = this.savedAccess || {};
            if (
              newCampaign.access_mode === "password" &&
              !saved.has_store_password
            ) {
              const mode = saved.access_mode || "open";
              newCampaign.access_mode = mode;
              newCampaign.storefront_password = mode === "password";
              newCampaign.is_restrict_email_domain = mode === "email_domain";
            }
          }
        }

        if (newCampaign._jv && newCampaign._jv.id) {
          try {
            await this.$axios.post(
              `/stores/${newCampaign._jv.id}/update_campaign`,
              {
                meta: {
                  method: "update_campaign",
                  args: {
                    newCampaign: newCampaign,
                  },
                },
              }
            );
          } catch (err) {
            this.isSaving = false;
            this.exitWithoutSavingModal = false;
            //this.$store.dispatch("raiseError", err);
            console.log(describeError(err));
            // A 400 is the server REFUSING the store as edited (a Store
            // Login dead end, an invalid domain, a bad date): nothing was
            // saved. Tagged so the autosave puts the saved state back on
            // screen instead of leaving the refused one showing (#344).
            const refusal = storeSaveRefusalMessage(err);
            if (refusal) {
              const refused = new Error(refusal);
              refused.storeSaveRefused = true;
              refused.cause = err;
              throw refused;
            }
            throw err;
          }
        }
        this.incrementSaveProgress(20);

        // Disable distributor dropdown list once campaign is saved
        this.$store.commit("setDistributorStatus", true);
        newCampaign.accounts = [];
        for (let i = 0; i < accountValues.length; i++) {
          // Classified by role SLUG, never by a role-id literal. The
          // previous ``role_id == 5`` is true only on a seeded database
          // (Shopper is 12 on production), so on production NOTHING
          // matched, the /campaign/updateShopper call never fired, and
          // edits to the shopper username and password were silently
          // discarded while the save reported success (#3579). The store's
          // shopper is saved through updateShopper above, before the row.
          //
          // "skip" covers the legacy Client Admin row -- no longer the
          // client-contact carrier since the Phase 1 migration moved the
          // contact to the first-class Client on stores.client_id, so a
          // stray row must not be resurrected here -- plus Super Admin
          // and Sales Executive, which are not store-scoped accounts.
          const disposition = classifyStoreAccount(accountValues[i]);
          if (disposition === "shopper" || disposition === "skip") {
            continue;
          } else {
            try {
              if (
                accountValues[i].username.length == 0 &&
                accountValues[i]._jv &&
                accountValues[i]._jv.id
              ) {
                let result = await this.$axios.get(
                  `/account/deleteAccount/${newCampaign._jv.id}/${accountValues[i]._jv.id}`
                );
              } else if (/^\w*$/.test(accountValues[i].username)) {
                let model = await this.updateOrCreateModel(
                  accountValues[i],
                  "accounts"
                );
                if (model) {
                  newCampaign.accounts.push(model);
                }
              } else {
                newCampaign.accounts.push(accountValues[i]);
              }
            } catch (err) {
              //this.$store.dispatch("raiseError", err);
              console.log(describeError(err));
              //determine what account already exists (admin buyer or shopper)
              //and make that tab invalid. By slug for the same reason as
              //the dispatch above -- a role-id literal mislabels which
              //tab is flagged on production.
              if (classifyStoreAccount(accountValues[i]) === "shopper") {
                this.incompleteTabs["shopper"] = true;
              } else {
                this.incompleteTabs["clientContact"] = true;
              }
              if (!this.editMode) {
                // Remove Added Accounts
                for (let j = 0; j < newCampaign.accounts.length; j++) {
                  let result = await this.$axios.get(
                    `/account/deleteAccount/${newCampaign._jv.id}/${newCampaign.accounts[j]._jv.id}`
                  );
                }
              }
              throw err;
            }
          }
        }

        this.incrementSaveProgress(5);

        // // update or create campaign products. do not try to update models that have already
        // // been ordered--that will cause the API to come back with a 400
        // if (newCampaign.campaign_products) {
        //   let productsOrdered = newCampaign.campaign_products.filter(
        //     (obj) => obj.is_ordered
        //   );
        //   newCampaign.campaign_products.push(...productsOrdered);
        // }
        let locationsSame = _.isEqual(
          newCampaign.locations,
          this.initialLocations
        );
        // update or create locations
        if (newCampaign.locations && this.isCampaignAvailable) {
          newCampaign.locations = await this.updateOrCreateModels(
            newCampaign.locations,
            "locations"
          );
        }
        this.incrementSaveProgress(5);

        // Client contact -- first-class Client (legacy-client ->
        // first-class-Client migration, Phase 1).
        //
        // StoreClientContact.updateCampaign() set ``newCampaign.client_id``
        // when the Distributor picked or created a Client. We persist
        // that via the Client<->Store attach endpoint
        // (POST /clients/<id>/stores) -- NOT the legacy
        // /campaign/updateAdminBuyer path, which ran a GLOBAL
        // email-uniqueness check ("Client email already exists") that
        // blocked legitimate one-client-many-stores reuse.
        if (newCampaign._jv && newCampaign._jv.id) {
          const storeId = newCampaign._jv.id;
          const newClientId = newCampaign.client_id;
          const oldClientId = this._persistedClientId;
          // Pure decision helper (see clientContactSave.js): returns the
          // attach/detach plan. Routes ALL client linkage through the
          // guarded endpoints — true for both new and existing stores,
          // since the SAFRS create no longer writes stores.client_id.
          const plan = resolveClientContactChange(oldClientId, newClientId);
          if (plan.attach != null || plan.detach != null) {
            const clientsStore = useClientsStore();
            if (plan.attach != null) {
              // Attach to the newly-selected client first so the store is
              // never left without a contact if a detach later fails.
              // A failed attach is fatal — the user's selection didn't
              // persist, so surface it.
              await clientsStore.addStoreAssociation(plan.attach, storeId);
              // Record the attach as persisted BEFORE attempting the
              // detach, so a transient detach failure doesn't force a
              // full re-attach cycle on the next save.
              this._persistedClientId = plan.attach;
              this.usernameError = null;
              this.emailError = null;
            }
            if (plan.detach != null) {
              // Detach the previously-attached client. The endpoint is
              // idempotent / 409-guarded, so a transient failure is
              // self-healing; treat it as non-fatal (warn, don't throw)
              // — the new attach already succeeded and is persisted.
              try {
                await clientsStore.removeStoreAssociation(plan.detach, storeId);
              } catch (detachErr) {
                console.warn(
                  "Client detach failed (non-fatal; attach already persisted)",
                  describeError(detachErr)
                );
              }
            }
          }
        }
        this.incrementSaveProgress(5);

        // (The store's shopper login was saved above, before the row.)
        this.incrementSaveProgress(5);

        // update locations relationship
        if (newCampaign.locations && this.isCampaignAvailable) {
          await this.updateRelationship(
            newCampaign._jv.id,
            "locations",
            newCampaign.locations
          );
        }
        this.incrementSaveProgress(20);

        this.isSaved = true;
        // update campaign products SalesPrice with BLF cost
        // If product is saved, save BFL cost automatically

        if (
          this.campaign._jv &&
          this.campaign._jv.id &&
          this.campaign.bfl_cost
        ) {
          await this.$axios.get(
            `/campaign/updateCampaignProductsWithBFLCost/${newCampaign._jv.id}/${newCampaign.bfl_cost}`
          );
        }
        this.incrementSaveProgress(100);

        // The row and everything else saved; now report the store login
        // that did not (an explicit save's toast shows the refusal text).
        if (shopperRefusal) {
          this.isSaved = false;
          throw shopperRefusal;
        }
        if (shopperError) {
          this.isSaved = false;
          throw shopperError;
        }

        //if in preview tab, when saving, go to orders tab
        if (this.activeTab == 11) {
          //load orders tab before navigating
          this.tabRef(11)?.loadComponent();
          this.activeTab = this.availableTabs[
            this.availableTabs.indexOf(this.activeTab) + 1
          ];
          if (closeCampaignEdit) {
            this.$router.push("/distributor/stores");
            this.$store.commit("setEditStoreName", "");
          }
        }
        //else if campaign is saved in the name and launch date tab, keep the campaign edit page
        // else close the campaign editor and go back to home page
        else if (this.isCampaignAvailable && closeCampaignEdit) {
          this.$router.push("/distributor/stores");
          this.$store.commit("setEditStoreName", "");
        }
      } catch (err) {
        this.isSaved = false;
        console.log(describeError(err));
        if (err == "Client username already exists") {
          this.usernameError = err;
        } else if (err == "Client email already exists") {
          this.emailError = err;
        }
        throw err;
      } finally {
        this.isCampaignAvailable = true;
        this.isSaving = false;
        this.$store.commit("setProductsLoaded", false);
      }
    },

    close() {
      this.$router.push("/distributor/stores");
      this.$store.commit("setEditStoreName", "");
    },

    async updateOrCreateModel(model, modelUrl) {
      let newModel = null;
      if (model._jv && model._jv.id) {
        // newModel = await this.$store.dispatch("jv/patch", model);
        let identifier = {};
        identifier["id"] = model._jv.id;
        identifier["type"] = model._jv.type;
        identifier["attributes"] = model;
        let response = await this.$axios.patch(
          `/${modelUrl}/${identifier["id"]}/`,
          { data: identifier }
        );
        newModel = model;
      } else {
        if (model._jv && model._jv.type) {
          newModel = await this.$store.dispatch("jv/post", [
            model,
            { url: `/${modelUrl}` },
          ]);
        }
      }
      return newModel;
    },

    async updateOrCreateModels(modelList, modelUrl) {
      let updatedModels = [];
      for (const model of modelList) {
        this.incrementSaveProgress(3);
        let newModel;
        if (model._jv && model._jv.id) {
          // newModel = await this.$store.dispatch("jv/patch", model);
          let identifier = {};
          identifier["id"] = model._jv.id;
          identifier["type"] = model._jv.type;
          identifier["attributes"] = model;
          let response = await this.$axios.patch(
            `/${modelUrl}/${identifier["id"]}/`,
            { data: identifier }
          );
          newModel = model;
          updatedModels.push(newModel);
        } else {
          if (model._jv && model._jv.type) {
            newModel = await this.$store.dispatch("jv/post", [
              model,
              { url: `/${modelUrl}` },
            ]);
            updatedModels.push(newModel);
          }
        }
      }

      return updatedModels;
    },

    async updateRelationship(campaignId, relationship, modelList) {
      let listOfIdentifiers;
      if (relationship == "campaign_products") {
        listOfIdentifiers = modelList.map((obj) => {
          if (obj._jv) return { id: obj._jv.id, type: "CampaignProduct" };
          else return { id: obj.id, type: "CampaignProduct" };
        });
      } else {
        listOfIdentifiers = modelList.map((obj) => {
          if (obj._jv) return { id: obj._jv.id, type: obj._jv.type };
          else return { id: obj.id, type: obj.type };
        });
      }

      let response = await this.$axios.patch(
        `/stores/${campaignId}/${relationship}`,
        { data: listOfIdentifiers }
      );

      return response;
    },

    async getProductCount() {
      let count_result;
      if (this.campaign.id) {
        try {
          count_result = await this.$axios.get(
            `/product/getCampaignProductCount/${this.campaign._jv.id}/${true}`
          );
        } catch (err) {
          console.log(describeError(err));
          //this.$store.dispatch("raiseError", err);
          throw err;
        }
        this.product_count = count_result.data.count;
      }
    },

    async getUserHasAccessToCampaign(id) {
      let result;
      try {
        // The section being opened rides along: an ARCHIVED store answers
        // true for `batches` only (its restricted Batches-only mode).
        result = await this.$axios.get(
          `/campaign/getUserHasAccessToCampaign/${id}`,
          { params: { section: this.$route.params.section || undefined } }
        );
      } catch (err) {
        //this.$store.dispatch("raiseError", err);
        console.log(describeError(err));
      }
      if (result && result.data == false) {
        this.$router.push(`/`);
        this.$store.dispatch("raiseError", "Access Denied");
      }

      return result.data;
    },

    async loadCampaign(id = null) {
      this.campaignLoading = true;
      let result;
      if (!id) {
        // New-store (/stores/add) flow: there is no :id route param yet, so
        // there is nothing whose ownership we could check and nothing to
        // fetch -- the store does not exist until the first save. Running
        // the ownership check here sent GET
        // getUserHasAccessToCampaign/undefined, which the backend answers
        // False for any SCOPED caller (an impersonating superadmin or a
        // plain Owner). That false-denied every section click of a new
        // store with an "Access Denied" redirect. Treat "genuinely no id"
        // as "allowed, nothing to load" and return early. The
        // ownership/tenant-isolation guard below still runs for a real
        // existing store id.
        const routeCampaignId = this.$route.params.id;
        if (!routeCampaignId) {
          this.campaignLoading = false;
          return;
        }
        let hasAccess = await this.getUserHasAccessToCampaign(routeCampaignId);
        if (!hasAccess) {
          return;
        }
        result = await this.$axios.get(
          `/campaign/getCampaignById/${routeCampaignId}`
        );
      } else {
        let hasAccess = await this.getUserHasAccessToCampaign(id);
        if (!hasAccess) {
          return;
        }
        result = await this.$axios.get(`/campaign/getCampaignById/${id}`);
      }

      let campaign = result.data;
      campaign.accounts = Object.values(campaign.accounts);
      // campaign.campaign_products = Object.values(campaign.campaign_products);
      campaign.campaign_products = [];
      campaign.locations = Object.values(campaign.locations);
      this.initialLocations = _.cloneDeep(campaign.locations);
      this.campaign = campaign;
      // Snapshot the persisted client so the save path can detach a
      // replaced client (Phase 1 client-contact migration).
      this._persistedClientId = campaign.client_id != null ? campaign.client_id : null;

      this.isCampaignAvailable = true;
      //check if the campaign products already exist and enable products and preview tabs
      /*
      if (parseInt(count_result.data.count) > 0) {
        this.isCampaignAvailable = true;
      }
      */
      await this.getProductCount();
      await this.loadSavedAccess();
      this.campaignLoading = false;
    },

    /**
     * Read the store's SAVED Store Login state from the server. Called
     * after load and after every save, so the rail, Review & Launch and the
     * password field reflect what the server holds -- including whether a
     * shared password is stored, which the store row cannot say (#342).
     */
    async loadSavedAccess() {
      const id = this.campaign?._jv?.id;
      if (!id) return;
      try {
        const resp = await this.$axios.get(`/stores/${id}/access_state/`);
        if (resp && resp.data && typeof resp.data === "object") {
          this.savedAccess = resp.data;
        }
      } catch (err) {
        // Keep the last known state; the next save retries.
        console.warn("[CampaignAddEdit] could not read saved access state", describeError(err));
      }
    },

    /**
     * A save was REFUSED: nothing was written. Put the saved Store Login
     * slice back on screen (radio, rail, payment and tax settings) and say
     * why, inline. Then save again once, so every OTHER edit made with it
     * still lands -- the refused value is no longer in the store, so that
     * save can succeed (Bug Intake #344, #351; Jake 2026-10-15).
     */
    async revertToSavedAccess(reason) {
      await this.loadSavedAccess();
      const saved = this.savedAccess;
      // No reason means it is already shown elsewhere (Store Login's inline
      // store-login refusal), so the banner is left as it is.
      if (reason) this.saveRefusal = reason;
      if (!saved || !this.campaign) return;
      for (const field of SAVED_ACCESS_FIELDS) {
        if (field in saved) {
          this.campaign[field] = Array.isArray(saved[field])
            ? [...saved[field]]
            : saved[field];
        }
      }
      // Sections are reached the way the autosave reaches them (a section
      // that is not mounted holds nothing to revert).
      for (const name of ["shopper", "features"]) {
        this.tabRef(this.tabNames.indexOf(name))?.adoptSavedAccess?.(saved);
      }
      if (!this._retriedAfterRefusal && !this._destroyed) {
        this._retriedAfterRefusal = true;
        this._autoSavePending = true;
      }
    },

    /**
     * A save was REFUSED over the Checkout section's slice: nothing was
     * written. Read the store as SAVED, put the Checkout slice back on the
     * editor's copy, and have the section re-hydrate from it and say why,
     * inline -- so the screen never shows a setting the server rejected
     * (Bug Intake #450, Mike on AC Hawks: the radio kept "Ship to company
     * address" while the storefront still asked for an address). Then save
     * again once, so every OTHER edit made with it still lands.
     *
     * If the saved store cannot be read, nothing on screen can be trusted
     * to match it, so the reason goes to the editor-wide banner instead.
     */
    async revertToSavedCheckout(reason) {
      const id = this.campaign?._jv?.id;
      let saved = null;
      if (id) {
        try {
          const resp = await this.$axios.get(`/campaign/getCampaignById/${id}`);
          saved = resp && resp.data && typeof resp.data === "object" ? resp.data : null;
        } catch (err) {
          console.warn("[CampaignAddEdit] could not read the saved store", describeError(err));
        }
      }
      const section = this.tabRef(this.tabNames.indexOf("checkoutInfo"));
      if (!saved || !this.campaign || !section?.adoptSavedCheckout) {
        this.saveRefusal = reason;
        return;
      }
      for (const field of SAVED_CHECKOUT_FIELDS) {
        if (field in saved) {
          this.campaign[field] = Array.isArray(saved[field])
            ? JSON.parse(JSON.stringify(saved[field]))
            : saved[field];
        }
      }
      section.adoptSavedCheckout(reason);
      if (!this._retriedAfterRefusal && !this._destroyed) {
        this._retriedAfterRefusal = true;
        this._autoSavePending = true;
      }
    },

    //loads le roles
    async loadRoles() {
      let roles = await this.$store.dispatch("jv/get", "roles");

      Object.values(roles).forEach((role) => {
        this.$data.roles[role.name] = role;
      });
    },

    //updates each tab to valid based on value
    async updateTabInvalid(tabName, value) {
      this.invalidTabsKey++;
      //if first creating campaign, and on first tab before creating and saving campaign
      if (
        !this.editMode &&
        tabName == "campaignSetup" &&
        !this.isCampaignAvailable
      ) {
        this.campaignSetupValid = this.$refs.campaignSetup.valid;
      }

      //if editing already created campaign
      // if tab must be visited before validating, check if visited before validating
      else if (this.editMode && this.canValidateTab(tabName)) {
        this.incompleteTabs[tabName] = value;
        this.safeValidateTab(tabName);
      }
      //if creating new campaign
      //only show tab as invalid if they've already visited it
      else if (this.visitedTabs[this.tabNames.indexOf(tabName)]) {
        this.incompleteTabs[tabName] = value;
        this.safeValidateTab(tabName);
      }
      this.$store.commit("setTabsIncomplete", this.getTabsIncomplete());

      this.$forceUpdate();
    },

    getTabsIncomplete() {
      for (const key in this.incompleteTabs) {
        if (Object.hasOwnProperty.call(this.incompleteTabs, key)) {
          const tabIsIncomplete = this.incompleteTabs[key];
          if (tabIsIncomplete) {
            return true;
          }
        }
      }
      return false;
    },

    // The Client contact card removed the persisted link (DELETE already
    // done). Mirror it locally so the save path does not see a stale link.
    // The Client contact card picked, created or removed a client. The
    // store's Client Company Name defaults from the attached client (card
    // #499), so the field's default follows the pick before anything is
    // saved. A display-only mirror of the server's `linked_client_name`: it
    // is never sent back (the server resolves the default on read and
    // never writes it).
    onClientCompanyChanged(company) {
      this.campaign.linked_client_name = company || null;
    },

    onClientUnlinked() {
      this._persistedClientId = null;
      this.campaign.client_id = null;
    },

    // Resolve the nested StoreClientContact child (the Client contact card).
    // It is slotted into StoreSetup's #client-contact slot but its ref lives
    // on THIS parent (ref="clientContact", see the #section-client-store
    // template), so it's reachable via this.$refs. Wrapped in a method so
    // the save chain has a single, testable seam.
    _clientContactRef() {
      return this.$refs.clientContact;
    },

    async saveTab() {
      // have the current tab update our campaign model
      const activeTab = this.activeTab;
      let result;
      if (this.visitedTabs.length == 0) {
        this.visitedTabs[0] = true;
      }

      this.visitedTabs[activeTab] = true;

      // if the update throws an exception, catch it and pop a toaster message

      try {
        if (this.tabRef(activeTab))
          result = await this.tabRef(activeTab).updateCampaign(this.campaign);
      } catch (err) {
        this.$store.dispatch("raiseError", err);
        console.log(describeError(err));
      }
      if (result) {
        this.campaign = result;
      }

      // Merge the nested Client contact card (StoreClientContact) on EVERY
      // save. On the Client & Store page the active top-level tab is the
      // SETUP section (campaignSetup / StoreSetup) whose updateCampaign()
      // does NOT touch client_id -- the client picker lives in the nested
      // StoreClientContact child, whose updateCampaign() is the only thing
      // that sets campaign.client_id. Without this explicit call the
      // picked client never reaches the attach step
      // (resolveClientContactChange) and the selection silently fails to
      // persist. Guarded: clientContact is only mounted while the
      // client-store section is active, so skip when the ref is absent
      // (other tabs) and never crash. Runs for new AND existing stores --
      // the attach block handles both.
      const clientContact = this._clientContactRef();
      if (clientContact && typeof clientContact.updateCampaign === "function") {
        try {
          const merged = await clientContact.updateCampaign(this.campaign);
          if (merged) {
            this.campaign = merged;
          }
        } catch (err) {
          this.$store.dispatch("raiseError", err);
          console.log(describeError(err));
        }
      }

      //this.updateTabInvalid(this.tabNames[activeTab], false);
      return true;
    },

    async nextTab(tabId, selectedBatch = null) {
      if (tabId == 13 || tabId == 12) {
        this.setBackgroundColor("#faf5f5");
        this.$refs["reports"].loadBatches();
        if (selectedBatch) {
          this.$refs["reports"].updateSelectedBatch(selectedBatch);
        }
      } else {
        this.setBackgroundColor("#FFFFFF");
      }
      if (this.activeTab === 0 && !this.editMode && !this.isCampaignAvailable) {
        await this.saveTab();
        await this.saveCampaign(false);
        this.routeToStore();
      }
      if (tabId != 10) {
        this.actionButtonsStatus = true;
      } else {
        this.actionButtonsStatus = this.$store.getters.getEditProductStatus;
      }

      await this.saveTab();
      // allow jumping between tabs even if forms have bad data (and cant be saved). only allow
      // pressing the "next" button if the forms validate and can be saved.
      if (tabId !== undefined) {
        this.activeTab = tabId;
      }
      //pressing next button
      else {
        this.activeTab = this.availableTabs[
          (this.availableTabs.indexOf(this.activeTab) + 1) %
            this.availableTabs.length
        ];
      }

      // perform any actions required to get the tab loaded
      if (this.tabRef(this.activeTab)?.loadComponent) {
        this.tabRef(this.activeTab).loadComponent();
      }

      // check if the account name is exist
      if (this.activeTab == 1 || this.activeTab == 2) {
        this.tabRef(this.activeTab)?.inputUsername();
      }

      if (this.activeTab == 10) {
        this.tabRef(10)?.loadCampaignProducts();
        this.tabRef(11)?.stopLoadingProducts();
        this.tabRef(11)?.clearProducts();
      } else if (this.activeTab == 11) {
        this.tabRef(11)?.loadCampaignProducts();
        this.tabRef(10)?.stopLoadingProducts();
        this.tabRef(10)?.clearProducts();
      } else if (this.activeTab == 12) {
        this.tabRef(12)?.loadAllProducts();
        this.tabRef(11)?.stopLoadingProducts();
        this.tabRef(11)?.clearProducts();
      } else {
        this.tabRef(10)?.stopLoadingProducts();
        this.tabRef(11)?.stopLoadingProducts();
        this.tabRef(10)?.clearProducts();
        this.tabRef(11)?.clearProducts();
      }
    },

    async prevTab() {
      // only move to the previous tab if the data on the current form can be saved. it doesn't
      // matter if it passes validation.
      this.activeTab = this.availableTabs[
        this.availableTabs.indexOf(this.activeTab) - 1
      ];

      // perform any actions required to get the tab loaded
      if (this.tabRef(this.activeTab)?.loadComponent) {
        this.tabRef(this.activeTab).loadComponent();
      }
      // check if the account name is exist
      if (this.activeTab == 1 || this.activeTab == 2) {
        this.tabRef(this.activeTab)?.inputUsername();
      }
      if (this.activeTab < 10) {
        this.tabRef(10)?.clearProducts();
        this.tabRef(11)?.clearProducts();
      } else if (this.activeTab == 10) {
        this.tabRef(this.activeTab)?.loadCampaignProducts();
        this.tabRef(11)?.clearProducts();
      } else if (this.activeTab == 11) {
        this.tabRef(this.activeTab)?.loadCampaignProducts();
        this.tabRef(10)?.clearProducts();
      }
    },

    exitWithoutSavingCallback() {
      this.$store.commit("setEditStoreName", "");
      this.$store.commit("setCollectSalesTaxEnabled", false);
      this.exitWithoutSavingModal = false;
      this.$router.push("/distributor/stores");
    },

    async exitClick() {
      // Completed campaigns are read-only — exit immediately, no save prompt
      if (this.campaign.complete) {
        this.$store.commit("setProductsLoaded", false);
        this.$router.push("/distributor/stores");
        this.$store.commit("setEditStoreName", "");
        return;
      }
      if (this.isSaved) {
        this.$router.push("/distributor/stores");
        this.$store.commit("setEditStoreName", "");
      } else {
        // if campaign has a distributor, campaign name and company name, allow the user to save
        // tabRef(0) may be undefined if the user never visited the Setup
        // section in the redesigned editor — optional-chain accordingly.
        const setup = this.tabRef(0);
        if (
          setup?.distributorId &&
          setup?.campaignName &&
          setup?.companyName
        ) {
          this.campaignSaved = true;
        } else {
          this.campaignSaved = false;
        }
        // new Promise((resolve) => {
        //   setTimeout(resolve, 100);
        // });
        if (!this.$store.getters.getBFLCostChanged) {
          this.exitWithoutSavingModal = true;
        } else {
          //await this.$store.commit("setBFLCostChanged", false);
          await this.$store.commit("setIsClickedExitButton", true);
        }
      }
      this.$store.commit("setProductsLoaded", false);
    },
  },
};
</script>

<style>
.active-tab {
  color: var(--tf-primary) !important;
}
/*
.v-tabs {
  border-bottom: 1px solid #119ae948;
}

.v-tabs__div {
  position: relative;
}

.v-tabs__div > .v-tabs__item > div {
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1;
}
*/

.v-tabs__container {
  height: 100% !important;
}

.v-input--switch .text-accent {
  color: rgba(0, 0, 0, 0.87) !important;
  caret-color: rgba(0, 0, 0, 0.87) !important;
  /* color: var(--tf-primary) !important; */
}

.theme--light.v-input--switch__thumb {
  color: black;
}

/* Team & Departments section card chrome (gap-closure chain). */
.team-section-card {
  margin-bottom: 24px;
  padding: 8px;
}
.team-section-card-title {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-size: 1.25rem;
  font-weight: 600;
  padding-bottom: 8px;
}
.team-section-card-subtitle {
  font-size: 0.875rem;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 4px;
}
</style>

<style scoped>
.tab-container {
  padding-top: 24px;
}

/* Spacing for the Pricing section's two SELF-HOSTED cards (Store
   Pricing, Shopper Allowance).

   EditorCard spaces its own stack with `.editor-card + .editor-card`,
   but each of these two cards sits inside its own `v-if` wrapper <div>,
   so the two `.editor-card` elements are never adjacent siblings and
   that rule never applied -- the cards butted together with zero gap
   (Jake, 2026-08-26).

   PR #2938 spaced them with `.pricing-section-card +
   .pricing-section-card`, which worked while the two wrappers WERE
   adjacent. They no longer are: Store Pricing moved to the top of the
   section, so campaign-features now sits between them and the `+`
   combinator never matches. Keeping that rule would have left a
   selector that silently matches nothing -- worse than no rule, because
   it reads as though the spacing is handled.

   So each wrapper now carries its OWN bottom margin. Bottom rather than
   top, deliberately: Store Pricing is the first thing in the section
   and a top margin would indent it from the section heading. Both
   neighbours below it (campaign-features' `.feature-card`, and the end
   of the section) already own their top spacing, so a bottom margin
   composes with them instead of fighting them.

   `--tf-sp-4` is the same 16px step EditorCard uses for its own
   sibling spacing, so the whole section stays on one rhythm. */
.pricing-section-card {
  margin-bottom: var(--tf-sp-4, 16px);
}

/* Batches section, shown before the store has been saved. */
.bm-needs-store {
  background: var(--tf-surface-light);
  border: 1px solid var(--tf-border);
  border-radius: 10px;
  padding: 16px 18px;
  font-size: 13.5px;
  color: var(--tf-fg-3);
  line-height: 1.6;
  max-width: 640px;
}

.tab-info {
  margin-bottom: 12px;
}

.blockquote {
  text-align: left !important;
  font-size: 18px;
  font-weight: bold;
  padding-left: 0px !important;
}

.tab-icon {
  font-size: 24px !important;
}

.tab-text {
  margin-bottom: 4px;
  margin-top: 4px;
  font-size: 14px !important;
  text-transform: uppercase;
  font-weight: 700;
}

.v-tab--selected .tab-text {
  color: var(--tf-primary);
}

.tab-bottom-bar {
  background-color: var(--tf-bg);
}

.toolbar-tabs {
  position: -webkit-sticky;
  position: sticky !important;
  top: 76px;
  z-index: 3;
  max-width: 98vw;
  background: inherit;
}

.link-bubble {
  margin-left: 24px;
  margin-top: 15px;
}
.container-div {
  padding: 24px;
}
.tab-container {
  position: relative;
}
.tab-notification {
  height: 15px;
  width: 15px;
  background-color: #0c19f0;
  border-radius: 50%;
  border: 3px solid #ffffff9c;
  position: absolute;
  top: 2px;
  right: 2px;
  z-index: 1;
}

.tab-content-wrapper {
  position: relative;
}
.exit-and-save-btn,
.exit-and-save-btn:hover {
  position: fixed !important;
  top: 8px;
  z-index: 1007;
  right: 15px;
}
.report-link-bubble {
  top: 46px;
  left: -188px;
  z-index: 2;
  position: relative;
}

.campaign-creator-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-top: 0 !important;
  overflow: visible !important;
}

.campaign-creator-actions {
  margin-top: auto;
}

/* Unsaved-changes navigation guard modal (plain in-tree overlay). */
.sae-unsaved-scrim {
  position: fixed;
  inset: 0;
  z-index: 2400;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  padding: 24px;
}

.sae-unsaved-card {
  width: 100%;
  max-width: 440px;
  background: var(--tf-bg);
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
  padding: 24px;
}

.sae-unsaved-title {
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
}

.sae-unsaved-body {
  margin: 0 0 20px;
  font-size: 14px;
  line-height: 1.5;
  color: #4a4a4a;
}

.sae-unsaved-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.sae-unsaved-btn {
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.sae-unsaved-btn--ghost {
  background: #f0f0f0;
  color: #1a1a1a;
}

.sae-unsaved-btn--danger {
  background: var(--tf-error-dark);
  color: var(--tf-bg);
}
</style>





